import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import ManualTeamsModal from '../components/ManualTeamsModal';
import ManualScheduleModal from '../components/ManualScheduleModal';
import ManualSquadModal from '../components/ManualSquadModal';
import MatchResultModal from '../components/MatchResultModal';
import {
  parseScheduleInstantAsUtc,
  formatScheduleTimeUtc,
  formatScheduleTimeBrowserLocal
} from '../utils/scheduleTime';

/** Same shapes as backend adminService (CricAPI + manual schedule JSON) */
function extractMatchListFlexible(seriesInfo: any): any[] {
  if (seriesInfo == null) return [];
  let si = seriesInfo;
  if (typeof si === 'string') {
    try {
      si = JSON.parse(si);
    } catch {
      return [];
    }
  }
  if (typeof si !== 'object') return [];
  const candidates = [
    si.data?.matchList,
    si.matchList,
    si.data?.matches,
    si.matches,
    si.data?.data?.matchList
  ];
  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
}

const AdminTournamentDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [tournament, setTournament] = useState<any>(null);
  const [seriesId, setSeriesId] = useState('');
  const [importStatus, setImportStatus] = useState<string | null>(null);
  const [lastImport, setLastImport] = useState<string | null>(null);
  const [importedData, setImportedData] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<
    | 'cricapi'
    | 'teams'
    | 'schedule'
    | 'points_table'
    | 'teams_squad'
    | 'raw'
    | 'squad'
    | 'match_management'
    | 'pickem_phases'
    | 'pickem_activity'
    | 'player_management'
    | 'boosters'
    | 'h2h'
    | 'fantasy_points'
  >('cricapi');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [teamsVisible, setTeamsVisible] = useState(false);
  const [scheduleVisible, setScheduleVisible] = useState(false);
  const [updatingVisibility, setUpdatingVisibility] = useState<string | null>(null);
  
  // Individual selection states
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [selectedMatches, setSelectedMatches] = useState<string[]>([]);
  const [visibleTeams, setVisibleTeams] = useState<string[]>([]);
  const [visibleMatches, setVisibleMatches] = useState<string[]>([]);
  const [updatingIndividual, setUpdatingIndividual] = useState<string | null>(null);

  // Series Squad states
  const [squadData, setSquadData] = useState<any>(null);
  const [squadLoading, setSquadLoading] = useState(false);
  const [squadError, setSquadError] = useState<string | null>(null);
  const [lastSquadImport, setLastSquadImport] = useState<string | null>(null);
  const [storedSquadData, setStoredSquadData] = useState<any>(null);
  const [budgetLeaguePopulateLoading, setBudgetLeaguePopulateLoading] = useState(false);
  const [budgetLeaguePopulateMessage, setBudgetLeaguePopulateMessage] = useState<string | null>(null);
  
  // Player selection states
  const [selectedPlayers, setSelectedPlayers] = useState<string[]>([]);
  const [visiblePlayers, setVisiblePlayers] = useState<string[]>([]);
  const [updatingPlayers, setUpdatingPlayers] = useState<string | null>(null);
  
  // Foreign player states
  const [selectedForeignPlayers, setSelectedForeignPlayers] = useState<string[]>([]);
  const [foreignPlayers, setForeignPlayers] = useState<string[]>([]);
  const [updatingForeignPlayers, setUpdatingForeignPlayers] = useState<string | null>(null);
  
  // Team tab state
  const [selectedTeamTab, setSelectedTeamTab] = useState<string>('');

  // Manual entry modals
  const [showManualTeamsModal, setShowManualTeamsModal] = useState(false);
  const [showManualScheduleModal, setShowManualScheduleModal] = useState(false);
  const [showManualSquadModal, setShowManualSquadModal] = useState(false);

  // Match Management states
  const [tournamentMatches, setTournamentMatches] = useState<any[]>([]);
  const [matchManagementFetchError, setMatchManagementFetchError] = useState<string | null>(null);
  const [matchManagementLoading, setMatchManagementLoading] = useState(false);
  const [importingMatches, setImportingMatches] = useState(false);
  const [updatingMatch, setUpdatingMatch] = useState<string | null>(null);
  const [showMatchResultModal, setShowMatchResultModal] = useState(false);
  const [selectedMatch, setSelectedMatch] = useState<any>(null);
  
  // Match Extension states
  const [matchExtensionStatuses, setMatchExtensionStatuses] = useState<{[key: string]: any}>({});
  const [extendingMatch, setExtendingMatch] = useState<string | null>(null);
  const [resettingMatch, setResettingMatch] = useState<string | null>(null);
  const [unlockingMatch, setUnlockingMatch] = useState<string | null>(null);

  // ESPN scorecard URL (for auto POM): per-match draft and saving state
  const [espnUrlDrafts, setEspnUrlDrafts] = useState<Record<string, string>>({});
  const [espnUrlSaving, setEspnUrlSaving] = useState<string | null>(null);
  const [venueDrafts, setVenueDrafts] = useState<Record<string, string>>({});
  const [venueSaving, setVenueSaving] = useState<string | null>(null);
  const [venueScrapeUrl, setVenueScrapeUrl] = useState('');
  const [venueScrapeDryRun, setVenueScrapeDryRun] = useState(true);
  const [venueScrapeLoading, setVenueScrapeLoading] = useState(false);

  // Run auto-result check now (same as 30-min cron)
  const [autoResultRunning, setAutoResultRunning] = useState(false);
  const [autoResultMessage, setAutoResultMessage] = useState<string | null>(null);

  // Player Management states
  const [playerCredits, setPlayerCredits] = useState<{[key: string]: number}>({});
  const [updatingCredits, setUpdatingCredits] = useState<string | null>(null);
  const [creditValue, setCreditValue] = useState<number>(9);
  const [teamFilter, setTeamFilter] = useState<string>('All');
  const [roleFilter, setRoleFilter] = useState<string>('All');

  // Boosters tab state
  const [availableBoosters, setAvailableBoosters] = useState<Array<{ id: number; name: string; description?: string; max_usage_per_tournament?: number; is_active?: boolean }>>([]);
  const [tournamentBoosters, setTournamentBoosters] = useState<Array<{ booster_id: number; booster_name: string; is_enabled: boolean; max_usage_per_league: number }>>([]);
  const [boosterSettings, setBoosterSettings] = useState<Record<number, { is_enabled: boolean; max_usage_per_league: number }>>({});
  const [boostersLoading, setBoostersLoading] = useState(false);
  const [boostersSaving, setBoostersSaving] = useState(false);
  const [boostersError, setBoostersError] = useState<string | null>(null);

  // H2H tab state
  const [h2hEnabled, setH2hEnabled] = useState(false);
  const [h2hAllowedModes, setH2hAllowedModes] = useState<string[]>(['single', 'multi']);
  const [h2hAllowedLengths, setH2hAllowedLengths] = useState<number[]>([3, 5, -1]);
  const [h2hLoading, setH2hLoading] = useState(false);
  const [h2hSaving, setH2hSaving] = useState(false);
  const [h2hError, setH2hError] = useState<string | null>(null);

  // Dynamic Pick'em — points table (scrape + aliases)
  const [pointsTableSnapshots, setPointsTableSnapshots] = useState<any[]>([]);
  const [pointsTableLoading, setPointsTableLoading] = useState(false);
  const [pointsTableMessage, setPointsTableMessage] = useState<string | null>(null);
  const [ptCricbuzzUrl, setPtCricbuzzUrl] = useState('');
  const [ptEspnUrl, setPtEspnUrl] = useState('');
  const [ptWikipediaUrl, setPtWikipediaUrl] = useState('');
  const [ptAliasesJson, setPtAliasesJson] = useState('{}');
  const [pointsTableScraping, setPointsTableScraping] = useState(false);
  const [pointsTableSavingConfig, setPointsTableSavingConfig] = useState(false);

  // Fantasy Points tab state
  const DEFAULT_FP_RULES = {
    duck_enabled: true, duck_points: -5, duck_min_balls: 10,
    sr_bonus_enabled: true, sr_bonus_min_balls: 10,
    sr_bonus_bands: [
      { min_sr: 100, max_sr: 110, points: 5 },
      { min_sr: 110, max_sr: 125, points: 10 },
      { min_sr: 125, max_sr: 150, points: 15 },
      { min_sr: 150, max_sr: 200, points: 20 },
      { min_sr: 200, max_sr: null, points: 25 },
    ],
    sr_penalty_enabled: true, sr_penalty_min_balls: 10,
    sr_penalty_bands: [
      { min_sr: 80, max_sr: 100, points: -5 },
      { min_sr: 60, max_sr: 80, points: -10 },
      { min_sr: null, max_sr: 60, points: -15 },
    ],
    economy_enabled: true, economy_min_overs: 1,
    economy_bands: [
      { min_economy: 9, max_economy: 10, points: 0 },
      { min_economy: 7, max_economy: 9, points: 5 },
      { min_economy: 6, max_economy: 7, points: 10 },
      { min_economy: null, max_economy: 6, points: 15 },
      { min_economy: 10, max_economy: 12, points: -5 },
      { min_economy: 12, max_economy: 14, points: -10 },
      { min_economy: 14, max_economy: null, points: -15 },
    ],
    pom_enabled: true, pom_bonus_points: 25,
    playing_xi_enabled: true, playing_xi_bonus_points: 5,
    winning_team_enabled: true, winning_team_bonus_points: 5,
  };
  const [fpRules, setFpRules] = useState<any>({ ...DEFAULT_FP_RULES });
  const [fpLoading, setFpLoading] = useState(false);
  const [fpSaving, setFpSaving] = useState(false);
  const [fpError, setFpError] = useState<string | null>(null);
  const [fpSuccess, setFpSuccess] = useState<string | null>(null);

  // Pick'em phase/week groups (per tournament; optional)
  type PickemPhaseRow = { id: string; label: string; match_ids: string[] };
  const [pickemPhasesDraft, setPickemPhasesDraft] = useState<PickemPhaseRow[]>([]);
  const [pickemPhaseEditId, setPickemPhaseEditId] = useState<string | null>(null);
  const [pickemPhasesSaving, setPickemPhasesSaving] = useState(false);
  const [pickemPhasesMessage, setPickemPhasesMessage] = useState<string | null>(null);

  type PickemAuditLeagueRow = { id: number; name: string; live_tournament_id?: number | null };
  const [pickemAuditLeagues, setPickemAuditLeagues] = useState<PickemAuditLeagueRow[]>([]);
  const [pickemAuditLeaguesLoading, setPickemAuditLeaguesLoading] = useState(false);
  const [pickemAuditLeaguesError, setPickemAuditLeaguesError] = useState<string | null>(null);
  const [pickemAuditLeagueId, setPickemAuditLeagueId] = useState('');
  const [pickemAuditManualLeagueId, setPickemAuditManualLeagueId] = useState('');
  const [pickemAuditFilterUserId, setPickemAuditFilterUserId] = useState('');
  const [pickemAuditFilterMatchId, setPickemAuditFilterMatchId] = useState('');
  const [pickemAuditLimit, setPickemAuditLimit] = useState('500');
  const [pickemAuditEvents, setPickemAuditEvents] = useState<any[]>([]);
  const [pickemAuditEventsLoading, setPickemAuditEventsLoading] = useState(false);
  const [pickemAuditEventsError, setPickemAuditEventsError] = useState<string | null>(null);

  // Load player credits when component mounts or when importedData changes
  useEffect(() => {
    if (importedData?.players && importedData.players.length > 0) {
      loadPlayerCredits();
    }
  }, [importedData?.players]);

  const loadPlayerCredits = async () => {
    try {
      const response = await apiService.getPlayerCredits(Number(id));
      if (response.success) {
        setPlayerCredits(response.credits || {});
      }
    } catch (error) {
      console.error('Error loading player credits:', error);
    }
  };

  // Set default team tab when teams are available
  useEffect(() => {
    if (importedData?.players && importedData.players.length > 0 && !selectedTeamTab) {
      const teams = Array.from(new Set(importedData.players.map((player: any) => player.team_name))).sort() as string[];
      if (teams.length > 0) {
        setSelectedTeamTab(teams[0]);
      }
    }
  }, [importedData?.players, selectedTeamTab]);

  // Fetch tournament info and last imported series info
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch tournament info (from your DB, not CricAPI)
      if (!id) {
        setError('Tournament ID is required');
        setLoading(false);
        return;
      }
      
      const tournamentId = parseInt(id);
      const tData = await apiService.getAdminTournamentById(tournamentId);
      if (tData.success) {
        setTournament((tData as any).tournament);
      }

      let initialImportedData: any = null;

      console.log('🔍 Frontend: Fetching series info for tournament:', id);
      let sData: any = { success: false };
      try {
        sData = await apiService.getTournamentSeriesInfo(tournamentId);
      } catch (seriesErr: any) {
        console.error('🔍 Frontend: Series info request failed:', seriesErr);
        setError((prev) =>
          prev
            ? `${prev} · Series info: ${seriesErr.message || 'unavailable'}`
            : `Series info unavailable (${seriesErr.message || 'request failed'}). Schedule/Teams tabs still work.`
        );
      }
      console.log('🔍 Frontend: Series info response:', { success: sData.success, hasData: !!sData.cricapi_series_info });

      if (sData.success) {
        if (sData.cricapi_series_id) {
          setSeriesId(sData.cricapi_series_id);
        }

        if (sData.cricapi_series_info) {
          setLastImport(sData.cricapi_series_info_imported_at);
          initialImportedData = parseSeriesInfo(sData.cricapi_series_info);
          setTeamsVisible(sData.teams_visible || false);
          setScheduleVisible(sData.schedule_visible || false);
          console.log('🔍 Frontend: Successfully loaded series info');
        } else {
          console.log('🔍 Frontend: No series info found, but tournament exists');
        }

        if (sData.cricapi_series_squad) {
          console.log('🔍 Frontend: Found squad data in API response');
          setStoredSquadData(sData.cricapi_series_squad);
          setSquadData(sData.cricapi_series_squad);
          setLastSquadImport(sData.cricapi_series_squad_imported_at);
          console.log('🔍 Frontend: Successfully loaded stored squad data');

          const parsedPlayers = parseSeriesSquad(sData.cricapi_series_squad);
          console.log('🔍 Frontend: Parsed players from stored squad data:', parsedPlayers.length, 'players');

          if (initialImportedData) {
            initialImportedData = {
              ...initialImportedData,
              players: parsedPlayers
            };
            console.log('🔍 Frontend: Combined with existing importedData, total players:', initialImportedData.players.length);
          } else {
            initialImportedData = {
              teams: [],
              schedule: [],
              players: parsedPlayers,
              raw: null
            };
            console.log('🔍 Frontend: Created new importedData with players:', initialImportedData.players.length);
          }
        } else {
          console.log('🔍 Frontend: No squad data found in API response');
        }
      } else if (sData?.error) {
        console.error('🔍 Frontend: Series info API error:', sData.error);
        setError((prev) =>
          prev ? `${prev} · ${sData.error}` : `Failed to load series info: ${sData.error}`
        );
      }

      if (tData.success) {
        if (!initialImportedData) {
          initialImportedData = { teams: [], schedule: [], players: [], raw: null };
        }
        setImportedData(initialImportedData);
        console.log('🔍 Frontend: importedData set for tabs');
      } else {
        setImportedData(null);
      }

      // Fetch individual visibility settings
      const vRes = await apiService.getVisibleItems(Number(id));
      if (vRes.success) {
        setVisibleTeams(vRes.visible_teams || []);
        setVisibleMatches(vRes.visible_matches || []);
        setVisiblePlayers(vRes.visible_players || []);
        setForeignPlayers(vRes.foreign_players || []);
      }

      // Fetch tournament matches for management
      await fetchTournamentMatches();
    } catch (err: any) {
      console.error('🔍 Frontend: Error in fetchData:', err);
      setError(`Failed to load data: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch tournament info and last imported series info on mount
  useEffect(() => {
    fetchData();
  }, [id]);

  // Load tournament matches when match_management or pickem_phases tab is selected
  useEffect(() => {
    if ((activeTab === 'match_management' || activeTab === 'pickem_phases') && id) {
      fetchTournamentMatches();
    }
  }, [activeTab, id]);

  // Initialize Pick'em phases draft when opening tab
  useEffect(() => {
    if (activeTab !== 'pickem_phases') return;
    const raw = tournament?.pickem_phase_groups;
    let parsed: any = raw;
    if (typeof raw === 'string') {
      try {
        parsed = JSON.parse(raw);
      } catch {
        parsed = null;
      }
    }
    const phases: PickemPhaseRow[] = Array.isArray(parsed?.phases)
      ? parsed.phases.map((p: any) => ({
          id: String(p.id || '').trim() || `phase-${Math.random().toString(36).slice(2, 8)}`,
          label: String(p.label || p.id || 'Phase').trim(),
          match_ids: Array.isArray(p.match_ids) ? p.match_ids.map((m: any) => String(m)) : []
        }))
      : [];
    setPickemPhasesDraft(phases);
    setPickemPhaseEditId(phases[0]?.id ?? null);
    setPickemPhasesMessage(null);
  }, [activeTab, tournament?.pickem_phase_groups]);

  // Pick'em fantasy leagues for this tournament (prediction activity audit tab)
  useEffect(() => {
    if (activeTab !== 'pickem_activity' || !id) return;
    const tournamentId = parseInt(id, 10);
    if (isNaN(tournamentId)) return;
    let cancelled = false;
    setPickemAuditLeaguesLoading(true);
    setPickemAuditLeaguesError(null);
    apiService
      .getAllLeaguesAdmin('pickem')
      .then((res: any) => {
        if (cancelled) return;
        const list: PickemAuditLeagueRow[] = Array.isArray(res?.leagues) ? res.leagues : [];
        const forTourney = list.filter((l) => Number(l.live_tournament_id) === tournamentId);
        setPickemAuditLeagues(forTourney);
        setPickemAuditLeagueId((prev) => {
          if (prev && forTourney.some((l) => String(l.id) === prev)) return prev;
          if (forTourney.length === 1) return String(forTourney[0].id);
          return '';
        });
      })
      .catch((err: any) => {
        if (!cancelled) setPickemAuditLeaguesError(err?.message || 'Failed to load Pick’em leagues');
      })
      .finally(() => {
        if (!cancelled) setPickemAuditLeaguesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeTab, id]);

  const loadPickemPredictionAudit = async () => {
    const lidRaw = pickemAuditManualLeagueId.trim() || pickemAuditLeagueId.trim();
    const leagueIdNum = parseInt(lidRaw, 10);
    if (!lidRaw || isNaN(leagueIdNum)) {
      setPickemAuditEventsError('Select a Pick’em league from the list, or enter a fantasy league ID.');
      return;
    }
    const uidRaw = pickemAuditFilterUserId.trim();
    const userIdOpt = uidRaw === '' ? undefined : parseInt(uidRaw, 10);
    if (uidRaw !== '' && isNaN(userIdOpt as number)) {
      setPickemAuditEventsError('User ID must be a number.');
      return;
    }
    const matchIdOpt = pickemAuditFilterMatchId.trim() || undefined;
    const lim = parseInt(pickemAuditLimit.trim(), 10);
    const limit = isNaN(lim) ? 500 : Math.min(2000, Math.max(1, lim));
    setPickemAuditEventsLoading(true);
    setPickemAuditEventsError(null);
    try {
      const res: any = await apiService.getPickemPredictionActivityAuditAdmin({
        leagueId: leagueIdNum,
        userId: userIdOpt,
        matchId: matchIdOpt,
        limit
      });
      const ev = Array.isArray(res?.events) ? res.events : [];
      setPickemAuditEvents(ev);
    } catch (e: any) {
      setPickemAuditEventsError(e?.message || 'Failed to load prediction activity');
      setPickemAuditEvents([]);
    } finally {
      setPickemAuditEventsLoading(false);
    }
  };

  // Load boosters when Boosters tab is selected
  useEffect(() => {
    if (activeTab !== 'boosters' || !id) return;
    const tournamentId = parseInt(id, 10);
    if (isNaN(tournamentId)) return;
    setBoostersLoading(true);
    setBoostersError(null);
    Promise.all([
      apiService.getAvailableBoosters(),
      apiService.getTournamentBoosters(tournamentId)
    ])
      .then(([availRes, tourRes]) => {
        const avail = (availRes as any)?.boosters ?? (availRes as any)?.data ?? [];
        const list = Array.isArray(avail) ? avail : [];
        setAvailableBoosters(list);
        const tour = (tourRes as any)?.boosters ?? (tourRes as any)?.data ?? [];
        const tourList = Array.isArray(tour) ? tour : [];
        setTournamentBoosters(tourList);
        const byId: Record<number, { is_enabled: boolean; max_usage_per_league: number }> = {};
        const tourMap = Object.fromEntries(tourList.map((t: any) => [t.booster_id, t]));
        list.forEach((b: any) => {
          const tid = b.id ?? b.booster_id;
          const t = tourMap[tid];
          byId[tid] = t
            ? { is_enabled: !!t.is_enabled, max_usage_per_league: Math.max(1, Number(t.max_usage_per_league) || 1) }
            : { is_enabled: false, max_usage_per_league: 1 };
        });
        setBoosterSettings(byId);
      })
      .catch((err: any) => {
        setBoostersError(err?.message || 'Failed to load boosters');
      })
      .finally(() => setBoostersLoading(false));
  }, [activeTab, id]);

  // Load H2H settings when H2H tab is selected
  useEffect(() => {
    if (activeTab !== 'h2h' || !id) return;
    const tournamentId = parseInt(id, 10);
    if (isNaN(tournamentId)) return;
    setH2hLoading(true);
    setH2hError(null);
    apiService.getTournamentH2hSettings(tournamentId)
      .then((res: any) => {
        setH2hEnabled(!!res.h2h_enabled);
        setH2hAllowedModes(Array.isArray(res.h2h_allowed_modes) ? res.h2h_allowed_modes : ['single', 'multi']);
        setH2hAllowedLengths(Array.isArray(res.h2h_allowed_lengths) ? res.h2h_allowed_lengths : [3, 5, -1]);
      })
      .catch((err: any) => setH2hError(err?.message || 'Failed to load H2H settings'))
      .finally(() => setH2hLoading(false));
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab !== 'points_table' || !id) return;
    const tournamentId = parseInt(id, 10);
    if (isNaN(tournamentId)) return;
    setPointsTableLoading(true);
    setPointsTableMessage(null);
    Promise.all([
      apiService.getTournamentPointsTableConfig(tournamentId),
      apiService.getTournamentPointsSnapshots(tournamentId)
    ])
      .then(([cfg, snaps]) => {
        const c = cfg as any;
        setPtCricbuzzUrl(c.cricbuzzUrl || '');
        setPtEspnUrl(c.espnUrl || '');
        setPtWikipediaUrl(c.wikipediaUrl || '');
        setPtAliasesJson(JSON.stringify(c.aliases && typeof c.aliases === 'object' ? c.aliases : {}, null, 2));
        const s = snaps as any;
        setPointsTableSnapshots(Array.isArray(s.snapshots) ? s.snapshots : []);
      })
      .catch((err: any) => setPointsTableMessage(err?.message || 'Failed to load points table'))
      .finally(() => setPointsTableLoading(false));
  }, [activeTab, id]);

  const saveH2hSettings = async () => {
    if (!id) return;
    const tournamentId = parseInt(id, 10);
    if (isNaN(tournamentId)) return;
    setH2hSaving(true);
    setH2hError(null);
    try {
      await apiService.updateTournamentH2hSettings(tournamentId, {
        h2h_enabled: h2hEnabled,
        h2h_allowed_modes: h2hAllowedModes,
        h2h_allowed_lengths: h2hAllowedLengths
      });
    } catch (err: any) {
      setH2hError(err?.message || 'Failed to save H2H settings');
    } finally {
      setH2hSaving(false);
    }
  };

  const toggleH2hMode = (mode: string) => {
    setH2hAllowedModes(prev => prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]);
  };

  const toggleH2hLength = (len: number) => {
    setH2hAllowedLengths(prev => prev.includes(len) ? prev.filter(l => l !== len) : [...prev, len]);
  };

  // Load Fantasy Point Rules when tab selected
  useEffect(() => {
    if (activeTab !== 'fantasy_points' || !id) return;
    const tournamentId = parseInt(id, 10);
    if (isNaN(tournamentId)) return;
    setFpLoading(true);
    setFpError(null);
    apiService.getFantasyPointRules(tournamentId)
      .then((res: any) => { if (res.success) setFpRules(res.rules); })
      .catch((err: any) => setFpError(err?.message || 'Failed to load fantasy point rules'))
      .finally(() => setFpLoading(false));
  }, [activeTab, id]);

  const saveFpRules = async () => {
    if (!id) return;
    const tournamentId = parseInt(id, 10);
    if (isNaN(tournamentId)) return;
    setFpSaving(true);
    setFpError(null);
    setFpSuccess(null);
    try {
      await apiService.updateFantasyPointRules(tournamentId, fpRules);
      setFpSuccess('Fantasy point rules saved successfully.');
    } catch (err: any) {
      setFpError(err?.message || 'Failed to save fantasy point rules');
    } finally {
      setFpSaving(false);
    }
  };

  const updateFpRule = (key: string, value: any) => {
    setFpRules((prev: any) => ({ ...prev, [key]: value }));
    setFpSuccess(null);
  };

  const updateBand = (bandKey: string, index: number, field: string, value: any) => {
    setFpRules((prev: any) => {
      const bands = [...(prev[bandKey] || [])];
      bands[index] = { ...bands[index], [field]: value === '' ? null : Number(value) };
      return { ...prev, [bandKey]: bands };
    });
    setFpSuccess(null);
  };

  // Monitor importedData changes for debugging
  useEffect(() => {
    console.log('🔍 Frontend: importedData state changed:', {
      hasData: !!importedData,
      teams: importedData?.teams?.length || 0,
      schedule: importedData?.schedule?.length || 0,
      players: importedData?.players?.length || 0
    });
  }, [importedData]);

  // Parse teams, schedule, players from series_info response
  function parseSeriesInfo(seriesInfo: any) {
    if (!seriesInfo) return null;
    let parsed = seriesInfo;
    if (typeof seriesInfo === 'string') {
      try {
        parsed = JSON.parse(seriesInfo);
      } catch {
        return null;
      }
    }
    if (typeof parsed !== 'object') return null;

    const matchList = extractMatchListFlexible(parsed);

    // --- TEAMS ---
    let teams: any[] = [];
    if (matchList.length > 0) {
      const allTeamInfos = matchList
        .map((match: any) => match.teamInfo)
        .filter((ti: any) => Array.isArray(ti))
        .flat();
      const seen = new Set<string>();
      if (allTeamInfos.length > 0) {
        teams = allTeamInfos.filter((team: any) => {
          const key = team.shortname || team.name;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      } else {
        for (const match of matchList) {
          const t1 = match.team1_name;
          const t2 = match.team2_name;
          if (t1 && !seen.has(t1)) {
            seen.add(t1);
            teams.push({ name: t1, shortname: t1 });
          }
          if (t2 && !seen.has(t2)) {
            seen.add(t2);
            teams.push({ name: t2, shortname: t2 });
          }
        }
      }

      teams.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    }

    // --- SCHEDULE ---
    let schedule: any[] = [];
    if (matchList.length > 0) {
      schedule = matchList.map((match: any) => {
        // Use the same ID generation logic as the backend
        const matchId = match.id || match.name;
        console.log('🔍 Frontend: Schedule parsing - match:', { originalId: match.id, name: match.name, generatedId: matchId });
        
        // Parse date and time from CricAPI data
        const dateTimeString = match.dateTimeGMT || match.date;
        let matchDate = null;
        let matchTime = null;
        let matchTimeLocal = null;

        if (dateTimeString) {
          try {
            const dateTime = parseScheduleInstantAsUtc(String(dateTimeString));
            if (dateTime) {
              matchDate = dateTime.toISOString().split('T')[0];
              matchTime = `${formatScheduleTimeUtc(dateTime)} UTC`;
              matchTimeLocal = formatScheduleTimeBrowserLocal(dateTime);
            }
          } catch (e) {
            console.error('Error parsing date/time:', e);
          }
        }
        
        const displayName =
          match.name ||
          `${match.team1_name || 'Team 1'} vs ${match.team2_name || 'Team 2'}`;

        return {
          id: matchId,
          name: displayName,
          date: matchDate || match.date || match.dateTimeGMT,
          time: matchTime,
          timeLocal: matchTimeLocal,
          venue: match.venue,
          status: match.status
        };
      });

      // Sort matches by date in ascending order
      schedule.sort((a, b) => {
        const dateA = new Date(a.date || 0);
        const dateB = new Date(b.date || 0);
        return dateA.getTime() - dateB.getTime();
      });
    }

    // --- PLAYERS (not present in this data, so leave as empty array for now) ---
    let players: any[] = [];

    return { teams, schedule, players, raw: parsed };
  }

  // Extract players from series_info as fallback when squad data is empty
  function extractPlayersFromSeriesInfo(seriesInfo: any) {
    if (!seriesInfo || typeof seriesInfo !== 'object') return [];
    
    const players: any[] = [];
    const seenPlayerIds = new Set<string>();
    
    try {
      // Parse if it's a string
      let parsedInfo = seriesInfo;
      if (typeof seriesInfo === 'string') {
        parsedInfo = JSON.parse(seriesInfo);
      }
      
      // Extract players from match teamInfo
      if (parsedInfo.data?.matchList && Array.isArray(parsedInfo.data.matchList)) {
        for (const match of parsedInfo.data.matchList) {
          if (match.teamInfo && Array.isArray(match.teamInfo)) {
            for (const team of match.teamInfo) {
              if (team.players && Array.isArray(team.players)) {
                for (const player of team.players) {
                  const playerId = player.id || player.playerId;
                  if (playerId && !seenPlayerIds.has(playerId)) {
                    seenPlayerIds.add(playerId);
                    const teamName = team.teamName || team.name || team.shortname || '';
                    players.push({
                      id: playerId,
                      name: player.name || player.playerName || 'Unknown Player',
                      team: teamName,
                      team_name: teamName,
                      role: player.role || player.playerRole || 'Player',
                      battingStyle: player.battingStyle || player.batting_style || null,
                      bowlingStyle: player.bowlingStyle || player.bowling_style || null,
                      country: player.country || null,
                      raw: player
                    });
                  }
                }
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error extracting players from series_info:', error);
    }
    
    return players;
  }

  // Parse players from series squad response
  function parseSeriesSquad(squadData: any) {
    if (!squadData || typeof squadData !== 'object') return [];

    console.log('🔍 Frontend: Parsing series squad data:', squadData);
    
    let players: any[] = [];
    
    // Handle different possible structures of squad data
    if (squadData.data && Array.isArray(squadData.data)) {
      // If data is an array of teams, each with a players array
      players = squadData.data.flatMap((team: any) =>
        Array.isArray(team.players)
          ? team.players.map((p: any) => {
              const teamName = team.teamName || team.shortname || team.name || team.shortName || '';
              return {
                ...p,
                team: teamName,
                team_name: teamName
              };
            })
          : []
      );
    } else if (squadData.data && squadData.data.squad) {
      // If data has a squad property
      players = squadData.data.squad;
    } else if (Array.isArray(squadData)) {
      // If the response is directly an array
      players = squadData;
    } else if (squadData.squad && Array.isArray(squadData.squad)) {
      // If squad is at the top level
      players = squadData.squad;
    }

    console.log('🔍 Frontend: Extracted players from squad data:', players);

    // Process each player to standardize the format
    const processedPlayers = players.map((player: any, index: number) => {
      // Generate a unique ID if not present
      const playerId = player.id || player.playerId || `${player.name}_${index}`;
      
      // Extract role-specific details
      let roleDetails = {};
      
      if (player.role === 'batsman' || player.battingStyle) {
        roleDetails = {
          ...roleDetails,
          battingStyle: player.battingStyle || player.batting_style || 'Right-handed',
          battingPosition: player.battingPosition || player.batting_position || 'Middle order'
        };
      }
      
      if (player.role === 'bowler' || player.bowlingStyle) {
        roleDetails = {
          ...roleDetails,
          bowlingStyle: player.bowlingStyle || player.bowling_style || 'Right-arm medium',
          bowlingType: player.bowlingType || player.bowling_type || 'Medium pace'
        };
      }
      
      if (player.role === 'all-rounder') {
        roleDetails = {
          ...roleDetails,
          battingStyle: player.battingStyle || player.batting_style || 'Right-handed',
          bowlingStyle: player.bowlingStyle || player.bowling_style || 'Right-arm medium'
        };
      }
      
      if (player.role === 'wicket-keeper') {
        roleDetails = {
          ...roleDetails,
          battingStyle: player.battingStyle || player.batting_style || 'Right-handed',
          keepingStyle: player.keepingStyle || player.keeping_style || 'Standard'
        };
      }

      return {
        id: playerId,
        name: player.name || player.playerName || 'Unknown Player',
        team: player.team || player.teamName || player.team_name || '',
        team_name: player.team_name || player.team || player.teamName || '',
        role: player.role || player.playerRole || player.player_role || 'Player',
        age: player.age || player.playerAge || null,
        country: player.country || player.nationality || null,
        battingStyle: player.battingStyle || player.batting_style || null,
        bowlingStyle: player.bowlingStyle || player.bowling_style || null,
        ...roleDetails,
        // Additional stats if available
        stats: player.stats || player.statistics || null,
        // Raw player data for reference
        raw: player
      };
    });

    // Sort players by team, then by role, then by name
    processedPlayers.sort((a, b) => {
      // First sort by team
      const teamA = (a.team || '').toLowerCase();
      const teamB = (b.team || '').toLowerCase();
      if (teamA !== teamB) return teamA.localeCompare(teamB);
      
      // Then sort by role priority
      const rolePriority: { [key: string]: number } = {
        'captain': 1,
        'vice captain': 2,
        'wicket keeper': 3,
        'batsman': 4,
        'all rounder': 5,
        'bowler': 6,
        'player': 7
      };
      const roleA = rolePriority[a.role?.toLowerCase() || ''] || 8;
      const roleB = rolePriority[b.role?.toLowerCase() || ''] || 8;
      if (roleA !== roleB) return roleA - roleB;
      
      // Finally sort by name
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    console.log('🔍 Frontend: Processed and sorted players:', processedPlayers);
    return processedPlayers;
  }

  // Handle import button click
  const handleFetchSeriesInfo = async () => {
    setImportStatus('Importing...');
    setError(null);
    try {
      if (!id) {
        setError('Tournament ID is required');
        setImportStatus(null);
        return;
      }
      const data = await apiService.importSeriesInfo(parseInt(id), seriesId);
      if (data.success) {
        setImportStatus('Import successful');
        setLastImport(data.importedAt);
        setImportedData(parseSeriesInfo(data.data));
      } else {
        setImportStatus(null);
        setError(data.error || 'Import failed');
      }
    } catch (err: any) {
      setImportStatus(null);
      setError(err.message || 'Import failed');
      console.error('Error importing series info:', err);
    }
  };

  // Handle series squad fetch and import button click
  const handleFetchSeriesSquad = async () => {
    if (!seriesId.trim()) {
      setSquadError('Please enter a Series ID');
      return;
    }
    
    if (!id) {
      setSquadError('Tournament ID is required');
      return;
    }
    
    setSquadLoading(true);
    setSquadError(null);
    try {
      console.log('🔍 Frontend: Importing series squad for series ID:', seriesId, 'tournament ID:', id);
      
      // Import the squad data to the database (this saves it)
      const response = await apiService.importSeriesSquad(parseInt(id), seriesId);
      console.log('🔍 Frontend: Series squad import response:', response);
      
      if (response.success) {
        // Fetch the updated tournament data to get the stored squad
        const sData = await apiService.getTournamentSeriesInfo(parseInt(id));
        if (sData.success && sData.cricapi_series_squad) {
          setSquadData(sData.cricapi_series_squad);
          setStoredSquadData(sData.cricapi_series_squad);
          setLastSquadImport(sData.cricapi_series_squad_imported_at || response.importedAt);
          
          // Parse the squad data and update importedData with players
          const parsedPlayers = parseSeriesSquad(sData.cricapi_series_squad);
          
          // If squad data is empty, try to extract players from series_info as fallback
          if (parsedPlayers.length === 0 && sData.cricapi_series_info) {
            console.log('⚠️ Frontend: Squad data is empty, trying to extract players from series_info');
            const playersFromSeriesInfo = extractPlayersFromSeriesInfo(sData.cricapi_series_info);
            if (playersFromSeriesInfo.length > 0) {
              console.log('✅ Frontend: Found', playersFromSeriesInfo.length, 'players from series_info');
              if (importedData) {
                setImportedData({
                  ...importedData,
                  players: playersFromSeriesInfo
                });
              } else {
                setImportedData({
                  teams: [],
                  schedule: [],
                  players: playersFromSeriesInfo,
                  raw: null
                });
              }
              setSquadError(`⚠️ Squad data was empty, but extracted ${playersFromSeriesInfo.length} players from series_info. You may want to use Manual Entry for complete player data.`);
            } else {
              setSquadError('⚠️ No players found in squad data. The series_squad endpoint returned empty data. Please try: 1) Verify the Series ID is correct, 2) Use Manual Entry to add players, or 3) Check if CricAPI has squad data for this series.');
            }
          } else {
            if (importedData) {
              setImportedData({
                ...importedData,
                players: parsedPlayers
              });
            } else {
              // If no importedData exists, create a new one with just players
              setImportedData({
                teams: [],
                schedule: [],
                players: parsedPlayers,
                raw: null
              });
            }
            console.log('🔍 Frontend: Successfully imported and parsed', parsedPlayers.length, 'players');
          }
          
          setActiveTab('teams_squad'); // Switch to teams squad tab to show the parsed data

          // Auto-sync to Budget League so tournament_players stays in sync with the new squad
          setBudgetLeaguePopulateMessage(null);
          try {
            const res = await apiService.populateBudgetLeagueTournamentPlayers(parseInt(id));
            const result = (res as any)?.result;
            const total = result?.total ?? 0;
            const inserted = result?.inserted ?? 0;
            const updated = result?.updated ?? 0;
            setBudgetLeaguePopulateMessage(`Budget League synced: ${inserted} new, ${updated} updated (${total} total players).`);
          } catch (e: any) {
            setBudgetLeaguePopulateMessage('Squad imported. Budget League sync failed: ' + (e?.message || 'Unknown error'));
          }
        } else {
          setSquadError('Failed to retrieve squad data after import');
        }
      } else {
        setSquadError(response.error || 'Failed to import series squad');
      }
    } catch (err: any) {
      console.error('🔍 Frontend: Error importing series squad:', err);
      setSquadError(err.message || 'Failed to import series squad');
    } finally {
      setSquadLoading(false);
    }
  };

  // Handle teams visibility toggle
  const handleToggleTeamsVisibility = async () => {
    setUpdatingVisibility('teams');
    try {
      if (!id) {
        setError('Tournament ID is required');
        setUpdatingVisibility(null);
        return;
      }
      const data = await apiService.toggleTeamsVisibility(parseInt(id));
      if (data.success) {
        setTeamsVisible(data.teams_visible);
      } else {
        setError(data.error || 'Failed to toggle teams visibility');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle teams visibility');
      console.error('Error toggling teams visibility:', err);
    } finally {
      setUpdatingVisibility(null);
    }
  };

  // Handle schedule visibility toggle
  const handleToggleScheduleVisibility = async () => {
    setUpdatingVisibility('schedule');
    try {
      if (!id) {
        setError('Tournament ID is required');
        setUpdatingVisibility(null);
        return;
      }
      const data = await apiService.toggleScheduleVisibility(parseInt(id));
      if (data.success) {
        setScheduleVisible(data.schedule_visible);
      } else {
        setError(data.error || 'Failed to toggle schedule visibility');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to toggle schedule visibility');
      console.error('Error toggling schedule visibility:', err);
    } finally {
      setUpdatingVisibility(null);
    }
  };

  // Handle individual team selection
  const handleTeamSelection = (teamId: string) => {
    console.log('🔍 Frontend: Team selection clicked:', teamId);
    setSelectedTeams(prev => {
      const newSelection = prev.includes(teamId) 
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId];
      console.log('🔍 Frontend: Updated team selection:', newSelection);
      return newSelection;
    });
  };

  // Handle individual match selection
  const handleMatchSelection = (matchId: string) => {
    console.log('🔍 Frontend: Match selection clicked:', matchId);
    setSelectedMatches(prev => {
      const newSelection = prev.includes(matchId) 
        ? prev.filter(id => id !== matchId)
        : [...prev, matchId];
      console.log('🔍 Frontend: Updated match selection:', newSelection);
      return newSelection;
    });
  };

  // Handle show selected teams to users
  const handleShowSelectedTeams = async () => {
    console.log('🔍 Frontend: Show selected teams clicked:', { selectedTeams, visibleTeams });
    if (selectedTeams.length === 0) {
      setError('Please select at least one team');
      return;
    }
    
    setUpdatingIndividual('teams');
    try {
      // Add selected teams to existing visible teams (append, don't replace)
      const newVisibleTeams = Array.from(new Set([...visibleTeams, ...selectedTeams]));
      console.log('🔍 Frontend: New visible teams array (appended):', newVisibleTeams);
      console.log('🔍 Frontend: About to call API with tournamentId:', Number(id), 'and teams:', newVisibleTeams);
      const result = await apiService.updateVisibleTeams(Number(id), newVisibleTeams);
      console.log('🔍 Frontend: API result:', result);
      if (result.success) {
        setVisibleTeams(newVisibleTeams);
        setSelectedTeams([]);
        setError(null);
        console.log('✅ Frontend: Successfully updated visible teams');
      } else {
        console.error('❌ Frontend: API returned error:', result.error);
        setError(result.error || 'Failed to update visible teams');
      }
    } catch (err: any) {
      console.error('❌ Frontend: Error updating visible teams:', err);
      setError('Failed to update visible teams');
    } finally {
      setUpdatingIndividual(null);
    }
  };

  // Handle show selected matches to users
  const handleShowSelectedMatches = async () => {
    console.log('🔍 Frontend: Show selected matches clicked:', { selectedMatches, visibleMatches });
    if (selectedMatches.length === 0) {
      setError('Please select at least one match');
      return;
    }
    
    setUpdatingIndividual('matches');
    try {
      // Add selected matches to existing visible matches (append, don't replace)
      const newVisibleMatches = Array.from(new Set([...visibleMatches, ...selectedMatches]));
      console.log('🔍 Frontend: New visible matches array (appended):', newVisibleMatches);
      console.log('🔍 Frontend: About to call API with tournamentId:', Number(id), 'and matches:', newVisibleMatches);
      const result = await apiService.updateVisibleMatches(Number(id), newVisibleMatches);
      console.log('🔍 Frontend: API result:', result);
      if (result.success) {
        setVisibleMatches(newVisibleMatches);
        setSelectedMatches([]);
        setError(null);
        console.log('✅ Frontend: Successfully updated visible matches');
      } else {
        console.error('❌ Frontend: API returned error:', result.error);
        setError(result.error || 'Failed to update visible matches');
      }
    } catch (err: any) {
      console.error('❌ Frontend: Error updating visible matches:', err);
      setError('Failed to update visible matches');
    } finally {
      setUpdatingIndividual(null);
    }
  };

  // Handle hide selected teams from users
  const handleHideSelectedTeams = async () => {
    if (selectedTeams.length === 0) {
      setError('Please select at least one team');
      return;
    }
    
    setUpdatingIndividual('teams');
    try {
      const newVisibleTeams = visibleTeams.filter(id => !selectedTeams.includes(id));
      const result = await apiService.updateVisibleTeams(Number(id), newVisibleTeams);
      if (result.success) {
        setVisibleTeams(newVisibleTeams);
        setSelectedTeams([]);
        setError(null);
      } else {
        setError(result.error || 'Failed to update visible teams');
      }
    } catch (err: any) {
      setError('Failed to update visible teams');
    } finally {
      setUpdatingIndividual(null);
    }
  };

  // Handle hide selected matches from users
  const handleHideSelectedMatches = async () => {
    if (selectedMatches.length === 0) {
      setError('Please select at least one match');
      return;
    }
    
    setUpdatingIndividual('matches');
    try {
      const newVisibleMatches = visibleMatches.filter(id => !selectedMatches.includes(id));
      const result = await apiService.updateVisibleMatches(Number(id), newVisibleMatches);
      if (result.success) {
        setVisibleMatches(newVisibleMatches);
        setSelectedMatches([]);
        setError(null);
      } else {
        setError(result.error || 'Failed to update visible matches');
      }
    } catch (err: any) {
      setError('Failed to update visible matches');
    } finally {
      setUpdatingIndividual(null);
    }
  };

  // Handle individual player selection
  const handlePlayerSelection = (playerId: string) => {
    console.log('🔍 Frontend: Player selection clicked:', playerId);
    setSelectedPlayers(prev => {
      const newSelection = prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId];
      console.log('🔍 Frontend: Updated player selection:', newSelection);
      return newSelection;
    });
  };

  // Handle show selected players to users
  const handleShowSelectedPlayers = async () => {
    console.log('🔍 Frontend: Show selected players clicked:', { selectedPlayers, visiblePlayers });
    if (selectedPlayers.length === 0) {
      setError('Please select at least one player');
      return;
    }
    
    setUpdatingPlayers('show');
    try {
      // Add selected players to existing visible players (append, don't replace)
      const newVisiblePlayers = Array.from(new Set([...visiblePlayers, ...selectedPlayers]));
      console.log('🔍 Frontend: New visible players array (appended):', newVisiblePlayers);
      const result = await apiService.updateVisiblePlayers(Number(id), newVisiblePlayers);
      console.log('🔍 Frontend: API result:', result);
      if (result.success) {
        setVisiblePlayers(newVisiblePlayers);
        setSelectedPlayers([]);
        setError(null);
        console.log('✅ Frontend: Successfully updated visible players');
      } else {
        console.error('❌ Frontend: API returned error:', result.error);
        setError(result.error || 'Failed to update visible players');
      }
    } catch (err: any) {
      console.error('❌ Frontend: Error updating visible players:', err);
      setError('Failed to update visible players');
    } finally {
      setUpdatingPlayers(null);
    }
  };

  // Handle hide selected players from users
  const handleHideSelectedPlayers = async () => {
    if (selectedPlayers.length === 0) {
      setError('Please select at least one player');
      return;
    }
    
    setUpdatingPlayers('hide');
    try {
      const newVisiblePlayers = visiblePlayers.filter(id => !selectedPlayers.includes(id));
      const result = await apiService.updateVisiblePlayers(Number(id), newVisiblePlayers);
      if (result.success) {
        setVisiblePlayers(newVisiblePlayers);
        setSelectedPlayers([]);
        setError(null);
      } else {
        setError(result.error || 'Failed to update visible players');
      }
    } catch (err: any) {
      setError('Failed to update visible players');
    } finally {
      setUpdatingPlayers(null);
    }
  };

  // Handle foreign player selection
  const handleForeignPlayerSelection = (playerId: string) => {
    console.log('🔍 Frontend: Foreign player selection clicked:', playerId);
    setSelectedForeignPlayers(prev => {
      const newSelection = prev.includes(playerId) 
        ? prev.filter(id => id !== playerId)
        : [...prev, playerId];
      console.log('🔍 Frontend: Updated foreign player selection:', newSelection);
      return newSelection;
    });
  };

  // Handle mark selected players as foreign
  const handleMarkSelectedAsForeign = async () => {
    console.log('🔍 Frontend: Mark selected as foreign clicked:', { selectedForeignPlayers, foreignPlayers });
    if (selectedForeignPlayers.length === 0) {
      setError('Please select at least one player');
      return;
    }
    
    setUpdatingForeignPlayers('mark');
    try {
      // Add selected players to foreign players list (append, don't replace)
      const newForeignPlayers = Array.from(new Set([...foreignPlayers, ...selectedForeignPlayers]));
      console.log('🔍 Frontend: New foreign players array (appended):', newForeignPlayers);
      const result = await apiService.updateForeignPlayers(Number(id), newForeignPlayers);
      console.log('🔍 Frontend: API result:', result);
      if (result.success) {
        setForeignPlayers(newForeignPlayers);
        setSelectedForeignPlayers([]);
        setError(null);
        console.log('✅ Frontend: Successfully updated foreign players');
      } else {
        console.error('❌ Frontend: API returned error:', result.error);
        setError(result.error || 'Failed to update foreign players');
      }
    } catch (err: any) {
      console.error('❌ Frontend: Error updating foreign players:', err);
      setError('Failed to update foreign players');
    } finally {
      setUpdatingForeignPlayers(null);
    }
  };

  // Handle unmark selected players as foreign
  const handleUnmarkSelectedAsForeign = async () => {
    if (selectedForeignPlayers.length === 0) {
      setError('Please select at least one player');
      return;
    }
    
    setUpdatingForeignPlayers('unmark');
    try {
      const newForeignPlayers = foreignPlayers.filter(id => !selectedForeignPlayers.includes(id));
      const result = await apiService.updateForeignPlayers(Number(id), newForeignPlayers);
      if (result.success) {
        setForeignPlayers(newForeignPlayers);
        setSelectedForeignPlayers([]);
        setError(null);
      } else {
        setError(result.error || 'Failed to update foreign players');
      }
    } catch (err: any) {
      setError('Failed to update foreign players');
    } finally {
      setUpdatingForeignPlayers(null);
    }
  };

  // Manual entry handlers
  const handleAddManualTeams = async (teams: any[]) => {
    try {
      const response = await apiService.addManualTeams(Number(id), teams);
      if (response.success) {
        // Refresh the data to show the new teams
        await fetchData();
        alert('Teams added successfully!');
      } else {
        alert('Failed to add teams: ' + response.message);
      }
    } catch (error) {
      console.error('Error adding manual teams:', error);
      alert('Error adding teams. Please try again.');
    }
  };

  const handleAddManualSchedule = async (matches: any[]) => {
    try {
      const response = await apiService.addManualSchedule(Number(id), matches);
      if (response.success) {
        // Refresh the data to show the new matches
        await fetchData();
        alert('Schedule added successfully!');
      } else {
        alert('Failed to add schedule: ' + response.message);
      }
    } catch (error) {
      console.error('Error adding manual schedule:', error);
      alert('Error adding schedule. Please try again.');
    }
  };

  const handleAddManualSquad = async (squad: any[]) => {
    try {
      const response = await apiService.addManualSquad(Number(id), squad);
      if (response.success) {
        // Refresh the data to show the new squad
        await fetchData();
        alert('Squad added successfully!');
      } else {
        alert('Failed to add squad: ' + response.message);
      }
    } catch (error) {
      console.error('Error adding manual squad:', error);
      alert('Error adding squad. Please try again.');
    }
  };

  // Trash icon SVG
  const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-500 hover:text-red-700 cursor-pointer" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  // Add delete handlers
  const handleDeleteTeam = async (shortname: string) => {
    if (!window.confirm('Are you sure you want to delete this team?')) return;
    await apiService.deleteTeamFromTournament(String(id), shortname);
    await fetchData();
  };
  const handleDeleteMatch = async (matchId: string) => {
    if (!window.confirm('Are you sure you want to delete this match?')) return;
    await apiService.deleteMatchFromTournament(String(id), matchId);
    await fetchData();
  };
  const handleDeletePlayer = async (playerId: string) => {
    if (!window.confirm('Are you sure you want to delete this player?')) return;
    await apiService.deletePlayerFromTournament(String(id), playerId);
    await fetchData();
  };

  // Match Management Functions
  const fetchTournamentMatches = async () => {
    setMatchManagementLoading(true);
    setMatchManagementFetchError(null);
    try {
      const response = await apiService.getTournamentMatchesAdmin(Number(id));
      setTournamentMatches(response.matches || []);
    } catch (error: any) {
      console.error('Failed to fetch tournament matches:', error);
      setMatchManagementFetchError(error?.message || 'Failed to load matches');
      setTournamentMatches([]);
    } finally {
      setMatchManagementLoading(false);
    }
  };

  const togglePickemMatchForPhase = (matchId: string, checked: boolean) => {
    const mid = String(matchId);
    const pid = pickemPhaseEditId;
    if (!pid) return;
    setPickemPhasesDraft((prev) => {
      const stripped = prev.map((p) => ({
        ...p,
        match_ids: p.match_ids.filter((m) => m !== mid)
      }));
      if (!checked) return stripped;
      return stripped.map((p) =>
        p.id === pid ? { ...p, match_ids: [...p.match_ids, mid] } : p
      );
    });
  };

  const addPickemPhase = () => {
    const n = pickemPhasesDraft.length + 1;
    const newId = `phase_${Date.now()}`;
    setPickemPhasesDraft((p) => [...p, { id: newId, label: `Phase ${n}`, match_ids: [] }]);
    setPickemPhaseEditId(newId);
  };

  const savePickemPhasesConfig = async () => {
    if (!id) return;
    setPickemPhasesSaving(true);
    setPickemPhasesMessage(null);
    try {
      const res = await apiService.updatePickemPhaseGroups(parseInt(id, 10), { phases: pickemPhasesDraft });
      if ((res as any).success) {
        setPickemPhasesMessage('Saved.');
        const tRes = await apiService.getAdminTournamentById(parseInt(id, 10));
        if ((tRes as any).success) setTournament((tRes as any).tournament);
      } else {
        setPickemPhasesMessage((res as any).error || 'Save failed');
      }
    } catch (e: any) {
      setPickemPhasesMessage(e?.message || 'Save failed');
    } finally {
      setPickemPhasesSaving(false);
    }
  };

  const clearPickemPhasesConfig = async () => {
    if (!window.confirm('Remove all pickem phase groups for this tournament?')) return;
    if (!id) return;
    setPickemPhasesSaving(true);
    setPickemPhasesMessage(null);
    try {
      const res = await apiService.updatePickemPhaseGroups(parseInt(id, 10), null);
      if ((res as any).success) {
        setPickemPhasesDraft([]);
        setPickemPhaseEditId(null);
        setPickemPhasesMessage('Cleared.');
        const tRes = await apiService.getAdminTournamentById(parseInt(id, 10));
        if ((tRes as any).success) setTournament((tRes as any).tournament);
      } else {
        setPickemPhasesMessage((res as any).error || 'Clear failed');
      }
    } catch (e: any) {
      setPickemPhasesMessage(e?.message || 'Clear failed');
    } finally {
      setPickemPhasesSaving(false);
    }
  };

  const handleImportMatches = async () => {
    setImportingMatches(true);
    try {
      const response = await apiService.importTournamentMatches(Number(id));
      alert(`Successfully imported ${response.imported} matches from schedule!`);
      await fetchTournamentMatches();
    } catch (error: any) {
      alert('Failed to import matches: ' + (error.message || 'Unknown error'));
    } finally {
      setImportingMatches(false);
    }
  };

  const handleScrapeScheduleVenues = async () => {
    const u = venueScrapeUrl.trim();
    if (!u) {
      alert('Paste a Wikipedia URL (e.g. https://en.wikipedia.org/wiki/2026_Indian_Premier_League)');
      return;
    }
    setVenueScrapeLoading(true);
    try {
      const r = await apiService.scrapeTournamentScheduleVenues(Number(id), u, venueScrapeDryRun);
      const loose =
        (r.unmatchedDb?.length || 0) +
        (r.unmatchedScrapedCount || r.unmatchedScraped?.length || 0);
      const sampleDb = (r.unmatchedDb || []).slice(0, 5).map((x: any) => x.match_name).join('; ');
      alert(
        `${r.dryRun ? 'Dry run — no DB writes.\n' : ''}` +
          `Scraped ${r.scrapedFixtures} fixtures. ` +
          `${r.appliedCount} row(s) ${r.dryRun ? 'would update' : 'updated'}.\n` +
          (loose ? `Unmatched (check team names vs Wikipedia): ${loose}. ${sampleDb ? `Examples: ${sampleDb}` : ''}` : 'All rows matched.')
      );
      if (!venueScrapeDryRun) await fetchTournamentMatches();
    } catch (e: any) {
      alert(e?.message || 'Venue scrape failed');
    } finally {
      setVenueScrapeLoading(false);
    }
  };

  const handleUpdateMatchResult = async (matchId: string, matchData: any) => {
    setUpdatingMatch(matchId);
    try {
      await apiService.updateTournamentMatchResult(Number(id), matchId, matchData);
      alert('Match result updated successfully! All Pick\'em leagues will be updated automatically.');
      await fetchTournamentMatches();
      setShowMatchResultModal(false);
      setSelectedMatch(null);
    } catch (error: any) {
      alert('Failed to update match result: ' + (error.message || 'Unknown error'));
    } finally {
      setUpdatingMatch(null);
    }
  };

  const handleFetchMatchDetails = async (matchId: string) => {
    setUpdatingMatch(matchId);
    try {
      const result = await apiService.fetchMatchDetails(Number(id), matchId);
      alert(`Match details fetched successfully!\n\nMatch: ${result.match.name}\nType: ${result.match.type}\nStatus: ${result.match.status}\nWinner: ${result.match.winner || 'Not set'}\nTotal Runs: ${result.match.totalRuns}\nInnings: ${result.match.innings}`);
      await fetchTournamentMatches();
    } catch (error: any) {
      alert('Failed to fetch match details: ' + (error.message || 'Unknown error'));
    } finally {
      setUpdatingMatch(null);
    }
  };

  const handleFetchAndOpenModal = async (match: any) => {
    setUpdatingMatch(match.match_id);
    try {
      console.log('🔍 Starting fetch and open modal for match:', match.match_id);
      
      // Fetch match details first
      console.log('📊 Fetching match details...');
      const result = await apiService.fetchMatchDetails(Number(id), match.match_id);
      console.log('✅ Match details fetched:', result);
      
      // Get players for dropdown
      console.log('👥 Fetching players...');
      let playersResult = null;
      try {
        playersResult = await apiService.getMatchPlayers(Number(id), match.match_id);
        console.log('✅ Players fetched:', playersResult);
      } catch (playerError: any) {
        console.warn('⚠️ Could not fetch players:', playerError.message);
        // Continue without players - this is not critical
        playersResult = { players: [] };
      }
      
      // Update the match with fetched data
      const updatedMatch = {
        ...match,
        winner: result.match.winner || match.winner,
        total_runs: result.match.totalRuns || match.total_runs,
        status: result.match.status || match.status,
        fetchedData: result.match,
        players: playersResult?.players || []
      };
      
      console.log('🔍 Winner from result:', result.match.winner);
      console.log('🔍 Winner from match:', match.winner);
      console.log('🔍 Final winner:', updatedMatch.winner);
      console.log('🔍 Players array:', updatedMatch.players);
      console.log('🔍 Players array length:', updatedMatch.players.length);
      console.log('🔍 Players array type:', typeof updatedMatch.players);
      console.log('🔍 Is players array:', Array.isArray(updatedMatch.players));
      
      console.log('📝 Updated match data:', updatedMatch);
      
      setSelectedMatch(updatedMatch);
      setShowMatchResultModal(true);
      
      // Refresh the matches list
      await fetchTournamentMatches();
      
    } catch (error: any) {
      console.error('❌ Error in handleFetchAndOpenModal:', error);
      alert('Failed to fetch match details: ' + (error.message || 'Unknown error'));
    } finally {
      setUpdatingMatch(null);
    }
  };

  const openMatchResultModal = (match: any) => {
    setSelectedMatch(match);
    setShowMatchResultModal(true);
  };

  // Match Extension Functions
  const loadMatchExtensionStatus = async (matchId: string) => {
    try {
      const response = await apiService.getMatchExtensionStatus(matchId);
      if (response.success) {
        setMatchExtensionStatuses(prev => ({
          ...prev,
          [matchId]: response.match
        }));
      }
    } catch (error) {
      console.error('Error loading extension status:', error);
    }
  };

  const handleExtendMatch = async (matchId: string) => {
    setExtendingMatch(matchId);
    try {
      const response = await apiService.extendMatchLockTime(matchId);
      if (response.success) {
        alert(`✅ Match lock time extended by 15 minutes!\nNew lock time: ${new Date(response.new_lock_time).toLocaleString()}`);
        await loadMatchExtensionStatus(matchId);
      } else {
        alert('Failed to extend match: ' + (response.error || 'Unknown error'));
      }
    } catch (error: any) {
      alert('Failed to extend match: ' + (error.message || 'Unknown error'));
    } finally {
      setExtendingMatch(null);
    }
  };

  const handleResetMatch = async (matchId: string) => {
    if (!window.confirm('Reset match lock time to default (5 minutes before match start)?')) {
      return;
    }
    setResettingMatch(matchId);
    try {
      const response = await apiService.resetMatchLockTime(matchId);
      if (response.success) {
        alert('✅ Match lock time reset to default');
        await loadMatchExtensionStatus(matchId);
      } else {
        alert('Failed to reset match: ' + (response.error || 'Unknown error'));
      }
    } catch (error: any) {
      alert('Failed to reset match: ' + (error.message || 'Unknown error'));
    } finally {
      setResettingMatch(null);
    }
  };

  const handleUnlockAndExtend = async (matchId: string) => {
    if (!window.confirm('⚠️ This will DELETE all team snapshots for this match and restore transfers. Continue?')) {
      return;
    }
    
    // Get league ID from tournament (you may need to adjust this)
    const leagueId = window.prompt('Enter League ID for this tournament:');
    if (!leagueId) return;
    
    setUnlockingMatch(matchId);
    try {
      const response = await apiService.unlockMatchAndExtend(matchId, parseInt(leagueId));
      if (response.success) {
        alert(`✅ Match unlocked!\n${response.snapshots_deleted} snapshots deleted.\nNew lock time: ${new Date(response.new_lock_time).toLocaleString()}`);
        await loadMatchExtensionStatus(matchId);
      } else {
        alert('Failed to unlock match: ' + (response.error || 'Unknown error'));
      }
    } catch (error: any) {
      alert('Failed to unlock match: ' + (error.message || 'Unknown error'));
    } finally {
      setUnlockingMatch(null);
    }
  };

  // Load extension statuses when matches are loaded
  useEffect(() => {
    if (activeTab === 'match_management' && tournamentMatches.length > 0) {
      tournamentMatches.forEach((match: any) => {
        loadMatchExtensionStatus(match.match_id);
      });
    }
  }, [activeTab, tournamentMatches]);

  // Sorted for Match Management: no-result matches first, then by date (result-set matches at bottom)
  const sortedMatchesForManagement = useMemo(() => {
    return [...tournamentMatches].sort((a: any, b: any) => {
      const aHasResult = !!(a.result_updated_at || (a.status === 'completed' && a.winner));
      const bHasResult = !!(b.result_updated_at || (b.status === 'completed' && b.winner));
      if (aHasResult !== bHasResult) return aHasResult ? 1 : -1;
      const dA = new Date(a.match_date || 0).getTime();
      const dB = new Date(b.match_date || 0).getTime();
      return dA - dB;
    });
  }, [tournamentMatches]);

  // Player Management Functions
  const handleUpdatePlayerCredits = async (playerId: string, credits: number) => {
    setUpdatingCredits(playerId);
    try {
      const response = await apiService.updatePlayerCredits(Number(id), playerId, credits);
      if (response.success) {
        setPlayerCredits(prev => ({
          ...prev,
          [playerId]: credits
        }));
        setError(null);
      } else {
        setError(response.error || 'Failed to update player credits');
      }
    } catch (err: any) {
      setError('Failed to update player credits');
    } finally {
      setUpdatingCredits(null);
    }
  };

  const handleBulkUpdateCredits = async () => {
    if (selectedPlayers.length === 0) {
      setError('Please select at least one player');
      return;
    }
    
    setUpdatingCredits('bulk');
    try {
      const updates = selectedPlayers.map(playerId => ({
        playerId,
        credits: creditValue
      }));
      
      const response = await apiService.bulkUpdatePlayerCredits(Number(id), updates);
      if (response.success) {
        setPlayerCredits(prev => {
          const updated = { ...prev };
          selectedPlayers.forEach(playerId => {
            updated[playerId] = creditValue;
          });
          return updated;
        });
        setSelectedPlayers([]);
        setError(null);
      } else {
        setError(response.error || 'Failed to update player credits');
      }
    } catch (err: any) {
      setError('Failed to update player credits');
    } finally {
      setUpdatingCredits(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Admin Tournament Details</h1>
            <p className="text-gray-300">Manage tournament data, teams, players, and match results</p>
          </div>
        <button
          onClick={() => navigate('/tournaments')}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg flex items-center space-x-2 transition-colors duration-200 shadow-lg"
        >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Back to Tournaments</span>
        </button>
      </div>
      
      {error && (
        <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 shadow-lg">
          <div className="flex items-center space-x-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>{error}</span>
          </div>
        </div>
      )}
      
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500"></div>
          <span className="ml-3 text-white text-lg">Loading tournament data...</span>
        </div>
      ) : (
        <>
          {tournament && (
            <div className="mb-6 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 shadow-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <div>
                    <div className="text-sm text-gray-400">Tournament Name</div>
                    <div className="text-lg font-semibold text-white">{tournament.name}</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  <div>
                    <div className="text-sm text-gray-400">Year</div>
                    <div className="text-lg font-semibold text-white">{tournament.year}</div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Navigation Tabs */}
          <div className="mb-8">
            <div className="border-b border-gray-700">
              <nav className="-mb-px flex space-x-8 overflow-x-auto">
                <button 
                  onClick={() => setActiveTab('cricapi')} 
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === 'cricapi' 
                      ? 'border-indigo-500 text-indigo-400' 
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  CricAPI Import
                </button>
            {importedData != null && (
              <>
                    <button 
                      onClick={() => setActiveTab('teams')} 
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === 'teams' 
                          ? 'border-indigo-500 text-indigo-400' 
                          : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      Teams
                    </button>
                    <button 
                      onClick={() => setActiveTab('schedule')} 
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === 'schedule' 
                          ? 'border-indigo-500 text-indigo-400' 
                          : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      Schedule
                    </button>
                    <button 
                      onClick={() => setActiveTab('teams_squad')} 
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === 'teams_squad' 
                          ? 'border-indigo-500 text-indigo-400' 
                          : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      Teams Squad
                    </button>
                    <button 
                      onClick={() => setActiveTab('player_management')} 
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === 'player_management' 
                          ? 'border-indigo-500 text-indigo-400' 
                          : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      Player Management
                    </button>
                    <button 
                      onClick={() => setActiveTab('raw')} 
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === 'raw' 
                          ? 'border-indigo-500 text-indigo-400' 
                          : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      SeriesInfo Raw
                    </button>
                    <button 
                      onClick={() => setActiveTab('squad')} 
                      className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                        activeTab === 'squad' 
                          ? 'border-indigo-500 text-indigo-400' 
                          : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                      }`}
                    >
                      Series Squad Raw
                    </button>
              </>
            )}
                <button 
                  onClick={() => setActiveTab('match_management')} 
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === 'match_management' 
                      ? 'border-indigo-500 text-indigo-400' 
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  Match Management
                </button>
                <button
                  onClick={() => setActiveTab('pickem_phases')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === 'pickem_phases'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  Pick&apos;em phases
                </button>
                <button
                  onClick={() => setActiveTab('pickem_activity')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === 'pickem_activity'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  Pick&apos;em activity
                </button>
                <button
                  onClick={() => setActiveTab('points_table')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === 'points_table'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  Points table
                </button>
                <button 
                  onClick={() => setActiveTab('boosters')} 
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === 'boosters' 
                      ? 'border-indigo-500 text-indigo-400' 
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  Boosters
                </button>
                <button 
                  onClick={() => setActiveTab('h2h')} 
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === 'h2h' 
                      ? 'border-indigo-500 text-indigo-400' 
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  H2H
                </button>
                <button
                  onClick={() => setActiveTab('fantasy_points')}
                  className={`py-3 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${
                    activeTab === 'fantasy_points'
                      ? 'border-indigo-500 text-indigo-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  }`}
                >
                  Fantasy Points
                </button>
              </nav>
            </div>
          </div>
          
          {activeTab === 'cricapi' && (
            <div>
              <div className="mb-6 p-6 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 shadow-xl">
                <h2 className="text-xl font-bold text-white mb-4">CricAPI Data Import</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block mb-2 text-sm font-medium text-gray-300">CricAPI Series ID</label>
            <input
              type="text"
              value={seriesId}
              onChange={e => setSeriesId(e.target.value)}
                      className="w-full px-4 py-3 bg-white/10 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                      placeholder="Enter CricAPI Series ID (e.g., 12345)"
            />
                  </div>
                  <div className="flex space-x-3">
              <button
                onClick={handleFetchSeriesInfo}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 shadow-lg"
              >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                Fetch Series Info
              </button>
              <button
                onClick={handleFetchSeriesSquad}
                disabled={squadLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 shadow-lg"
              >
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                {squadLoading ? 'Importing...' : 'Import Series Squad'}
              </button>
              <button
                onClick={async () => {
                  if (!id) return;
                  setBudgetLeaguePopulateMessage(null);
                  setBudgetLeaguePopulateLoading(true);
                  try {
                    const res = await apiService.populateBudgetLeagueTournamentPlayers(parseInt(id));
                    const result = (res as any)?.result;
                    const total = result?.total ?? 0;
                    const inserted = result?.inserted ?? 0;
                    const updated = result?.updated ?? 0;
                    setBudgetLeaguePopulateMessage(`Budget League: ${inserted} new, ${updated} updated (${total} total players). Sri Lankan and other newly imported squads will now appear in Budget League.`);
                  } catch (e: any) {
                    setBudgetLeaguePopulateMessage(e?.message || 'Failed to sync players to Budget League.');
                  } finally {
                    setBudgetLeaguePopulateLoading(false);
                  }
                }}
                disabled={budgetLeaguePopulateLoading || !storedSquadData}
                title={!storedSquadData ? 'Import Series Squad first' : 'Sync current squad into Budget League player pool'}
                className="flex-1 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 shadow-lg"
              >
                {budgetLeaguePopulateLoading ? 'Syncing...' : 'Sync to Budget League'}
              </button>
            </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                    {importStatus && (
                      <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-green-300">{importStatus}</span>
                        </div>
                      </div>
                    )}
                    {squadError && (
                      <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg">
                        <div className="flex items-center space-x-2">
                          <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span className="text-red-300">{squadError}</span>
                        </div>
                      </div>
                    )}
                    {budgetLeaguePopulateMessage && (
                      <div className={`p-3 rounded-lg border ${budgetLeaguePopulateMessage.startsWith('Budget League:') ? 'bg-green-500/20 border-green-500/50' : 'bg-red-500/20 border-red-500/50'}`}>
                        <span className={budgetLeaguePopulateMessage.startsWith('Budget League:') ? 'text-green-300' : 'text-red-300'}>{budgetLeaguePopulateMessage}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    {lastImport && (
                      <div className="p-3 bg-blue-500/20 border border-blue-500/50 rounded-lg">
                        <div className="text-sm text-blue-300">Last Series Import</div>
                        <div className="text-xs text-blue-200">{new Date(lastImport).toLocaleString()}</div>
                      </div>
                    )}
                    {lastSquadImport && (
                      <div className="p-3 bg-purple-500/20 border border-purple-500/50 rounded-lg">
                        <div className="text-sm text-purple-300">Last Squad Import</div>
                        <div className="text-xs text-purple-200">{new Date(lastSquadImport).toLocaleString()}</div>
                      </div>
                    )}
                  </div>
                </div>
          </div>
          
          {importedData && (
                <div className="p-6 bg-green-500/20 border border-green-500/50 rounded-xl shadow-xl">
                  <div className="flex items-center space-x-3 mb-4">
                    <svg className="w-8 h-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <h3 className="text-xl font-bold text-green-300">CricAPI Data Available</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-white/10 rounded-lg">
                      <div className="text-2xl font-bold text-green-400">{importedData.teams?.length || 0}</div>
                      <div className="text-sm text-green-200">Teams</div>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-lg">
                      <div className="text-2xl font-bold text-green-400">{importedData.schedule?.length || 0}</div>
                      <div className="text-sm text-green-200">Matches</div>
                    </div>
                    <div className="text-center p-4 bg-white/10 rounded-lg">
                      <div className="text-2xl font-bold text-green-400">{importedData.players?.length || 0}</div>
                      <div className="text-sm text-green-200">Players</div>
                    </div>
                  </div>
                  <div className="mt-4 text-sm text-green-200">
                    Switch to the other tabs to view and manage this data.
              </div>
                </div>
              )}
            </div>
          )}
              
          {importedData && (
            <div>
              {activeTab === 'teams' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Teams Management</h2>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowManualTeamsModal(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        + Add Manually
                      </button>
                      <button
                        onClick={handleToggleTeamsVisibility}
                        disabled={updatingVisibility === 'teams'}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          teamsVisible 
                            ? 'bg-red-600 hover:bg-red-700 text-white' 
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        } ${updatingVisibility === 'teams' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {updatingVisibility === 'teams' ? 'Updating...' : teamsVisible ? 'Hide All' : 'Show All'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-800 mb-2">
                      {teamsVisible ? '✅ Teams are visible to regular users' : '❌ Teams are hidden from regular users'}
                    </div>
                    <div className="text-xs text-blue-600">
                      Individual control: Select teams below and use Show/Hide buttons to control visibility per team.
                    </div>
                  </div>
                  
                  {selectedTeams.length > 0 && (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="text-sm text-yellow-800 mb-2">
                        {selectedTeams.length} team(s) selected
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleShowSelectedTeams}
                          disabled={updatingIndividual === 'teams'}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {updatingIndividual === 'teams' ? 'Updating...' : 'Show to Users'}
                        </button>
                        <button
                          onClick={handleHideSelectedTeams}
                          disabled={updatingIndividual === 'teams'}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {updatingIndividual === 'teams' ? 'Updating...' : 'Hide from Users'}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={selectedTeams.length === importedData.teams.length && importedData.teams.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedTeams(importedData.teams.map((team: any) => team.shortname || team.name));
                                } else {
                                  setSelectedTeams([]);
                                }
                              }}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Logo</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Short Name</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {importedData.teams.map((team: any, idx: number) => {
                          const teamId = team.shortname || team.name;
                          const isVisible = visibleTeams.includes(teamId);
                          return (
                              <tr key={teamId || idx} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={selectedTeams.includes(teamId)}
                                  onChange={() => handleTeamSelection(teamId)}
                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                              </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                {team.img && <img src={team.img} alt={teamId} className="w-8 h-8 rounded-full" />}
                              </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{team.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{team.shortname}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isVisible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {isVisible ? 'Visible' : 'Hidden'}
                                </span>
                              </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                <button onClick={() => handleDeleteTeam(team.shortname || team.name)} title="Delete Team"><TrashIcon /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'schedule' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Schedule Management</h2>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowManualScheduleModal(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        + Add Manually
                      </button>
                      <button
                        onClick={handleToggleScheduleVisibility}
                        disabled={updatingVisibility === 'schedule'}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          scheduleVisible 
                            ? 'bg-red-600 hover:bg-red-700 text-white' 
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        } ${updatingVisibility === 'schedule' ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        {updatingVisibility === 'schedule' ? 'Updating...' : scheduleVisible ? 'Hide All' : 'Show All'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-800 mb-2">
                      {scheduleVisible ? '✅ Schedule is visible to regular users' : '❌ Schedule is hidden from regular users'}
                    </div>
                    <div className="text-xs text-blue-600">
                      Individual control: Select matches below and use Show/Hide buttons to control visibility per match.
                    </div>
                  </div>
                  
                  {selectedMatches.length > 0 && (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="text-sm text-yellow-800 mb-2">
                        {selectedMatches.length} match(es) selected
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleShowSelectedMatches}
                          disabled={updatingIndividual === 'matches'}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {updatingIndividual === 'matches' ? 'Updating...' : 'Show to Users'}
                        </button>
                        <button
                          onClick={handleHideSelectedMatches}
                          disabled={updatingIndividual === 'matches'}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {updatingIndividual === 'matches' ? 'Updating...' : 'Hide from Users'}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                  <div className="overflow-x-auto">
                      <table className="min-w-full">
                        <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            <input
                              type="checkbox"
                              checked={selectedMatches.length === importedData.schedule.length && importedData.schedule.length > 0}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  const allMatchIds = importedData.schedule.map((match: any) => match.id || match.name);
                                  console.log('🔍 Frontend: Select all matches - IDs:', allMatchIds);
                                  setSelectedMatches(allMatchIds);
                                } else {
                                  console.log('🔍 Frontend: Deselect all matches');
                                  setSelectedMatches([]);
                                }
                              }}
                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                            />
                          </th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Match</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start (UTC)</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Your time</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venue</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visibility</th>
                            <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                        {importedData.schedule.map((match: any, idx: number) => {
                          const matchId = match.id || match.name;
                          const isVisible = visibleMatches.includes(matchId);
                          return (
                              <tr key={matchId || idx} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap">
                                <input
                                  type="checkbox"
                                  checked={selectedMatches.includes(matchId)}
                                  onChange={() => handleMatchSelection(matchId)}
                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                              </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{match.name}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{match.date}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">{match.time || 'TBD'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">{match.timeLocal || 'TBD'}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{match.venue}</td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    match.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    match.status === 'live' ? 'bg-yellow-100 text-yellow-800' :
                                    match.status === 'abandoned' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {match.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    isVisible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                }`}>
                                  {isVisible ? 'Visible' : 'Hidden'}
                                </span>
                              </td>
                                <td className="px-6 py-4 whitespace-nowrap text-center">
                                <button onClick={() => handleDeleteMatch(match.id || match.name)} title="Delete Match"><TrashIcon /></button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    </div>
                  </div>
                </div>
              )}
              
              {activeTab === 'teams_squad' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Teams Squad Management</h2>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => setShowManualSquadModal(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        + Add Manually
                      </button>
                      <button
                        onClick={() => setActiveTab('squad')}
                        className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium transition-colors"
                      >
                        View Raw Data
                      </button>
                    </div>
                  </div>
                  
                  {selectedPlayers.length > 0 && (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="text-sm text-yellow-800 mb-2">
                        {selectedPlayers.length} player(s) selected
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleShowSelectedPlayers}
                          disabled={updatingPlayers === 'show'}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {updatingPlayers === 'show' ? 'Updating...' : 'Show to Users'}
                        </button>
                        <button
                          onClick={handleHideSelectedPlayers}
                          disabled={updatingPlayers === 'hide'}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {updatingPlayers === 'hide' ? 'Updating...' : 'Hide from Users'}
                        </button>
                      </div>
                    </div>
                  )}

                  {selectedForeignPlayers.length > 0 && (
                    <div className="mb-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="text-sm text-orange-800 mb-2">
                        {selectedForeignPlayers.length} player(s) selected for foreign status
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={handleMarkSelectedAsForeign}
                          disabled={updatingForeignPlayers === 'mark'}
                          className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {updatingForeignPlayers === 'mark' ? 'Updating...' : 'Mark as Foreign'}
                        </button>
                        <button
                          onClick={handleUnmarkSelectedAsForeign}
                          disabled={updatingForeignPlayers === 'unmark'}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-3 py-1 rounded text-sm"
                        >
                          {updatingForeignPlayers === 'unmark' ? 'Updating...' : 'Unmark as Foreign'}
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {importedData.players && importedData.players.length > 0 ? (
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="flex">
                      {/* Team tabs on the left */}
                        <div className="w-64 bg-gray-50 p-4 border-r border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-800 mb-4">Teams</h3>
                        <div className="space-y-2">
                          {(() => {
                            const teams = Array.from(new Set(importedData.players.map((player: any) => player.team_name))).sort() as string[];
                            
                            return teams.map((team: string) => {
                              const teamPlayers = importedData.players.filter((player: any) => player.team_name === team);
                              const teamSelectedCount = teamPlayers.filter((player: any) => selectedPlayers.includes(player.id)).length;
                              const teamVisibleCount = teamPlayers.filter((player: any) => visiblePlayers.includes(player.id)).length;
                              const teamForeignCount = teamPlayers.filter((player: any) => foreignPlayers.includes(player.id)).length;
                              
                              return (
                                <button
                                  key={team}
                                  onClick={() => setSelectedTeamTab(team)}
                                  className={`w-full text-left p-3 rounded-lg transition-colors ${
                                    selectedTeamTab === team
                                        ? 'bg-indigo-600 text-white'
                                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                  }`}
                                >
                                  <div className="font-medium">{team}</div>
                                  <div className="text-xs mt-1 space-x-2">
                                      <span className="text-gray-500">{teamPlayers.length} players</span>
                                      <span className="text-green-600">{teamVisibleCount} visible</span>
                                      <span className="text-orange-600">{teamForeignCount} foreign</span>
                                      <span className="text-yellow-600">{teamSelectedCount} selected</span>
                                  </div>
                                </button>
                              );
                            });
                          })()}
                        </div>
                      </div>
                      
                      {/* Players table on the right */}
                        <div className="flex-1 bg-white p-4">
                        {selectedTeamTab ? (
                          <div>
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-semibold text-gray-800">{selectedTeamTab} Players</h3>
                              <div className="flex items-center space-x-2">
                                  <span className="text-sm text-gray-600">
                                  {importedData.players.filter((p: any) => p.team_name === selectedTeamTab).length} players
                                </span>
                              </div>
                            </div>
                            
                            <div className="overflow-x-auto">
                              <table className="min-w-full">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      <input
                                        type="checkbox"
                                        checked={
                                          importedData.players.filter((p: any) => p.team_name === selectedTeamTab).length > 0 &&
                                          importedData.players
                                            .filter((p: any) => p.team_name === selectedTeamTab)
                                            .every((p: any) => selectedPlayers.includes(p.id))
                                        }
                                        onChange={(e) => {
                                          const teamPlayerIds = importedData.players
                                            .filter((p: any) => p.team_name === selectedTeamTab)
                                            .map((p: any) => p.id);
                                          
                                          if (e.target.checked) {
                                            setSelectedPlayers([...selectedPlayers, ...teamPlayerIds]);
                                          } else {
                                            setSelectedPlayers(selectedPlayers.filter(id => !teamPlayerIds.includes(id)));
                                          }
                                        }}
                                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                    </th>
                                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                      <input
                                        type="checkbox"
                                        checked={
                                          importedData.players.filter((p: any) => p.team_name === selectedTeamTab).length > 0 &&
                                          importedData.players
                                            .filter((p: any) => p.team_name === selectedTeamTab)
                                            .every((p: any) => selectedForeignPlayers.includes(p.id))
                                        }
                                        onChange={(e) => {
                                          const teamPlayerIds = importedData.players
                                            .filter((p: any) => p.team_name === selectedTeamTab)
                                            .map((p: any) => p.id);
                                          
                                          if (e.target.checked) {
                                            setSelectedForeignPlayers([...selectedForeignPlayers, ...teamPlayerIds]);
                                          } else {
                                            setSelectedForeignPlayers(selectedForeignPlayers.filter(id => !teamPlayerIds.includes(id)));
                                          }
                                        }}
                                          className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                      />
                                      Foreign
                                    </th>
                                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Country</th>
                                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Details</th>
                                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                  </tr>
                                </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                  {importedData.players
                                    .filter((player: any) => player.team_name === selectedTeamTab)
                                    .map((player: any, idx: number) => {
                                      const isVisible = visiblePlayers.includes(player.id);
                                      const isForeign = foreignPlayers.includes(player.id);
                                      return (
                                          <tr key={player.id || idx} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                            <input
                                              type="checkbox"
                                              checked={selectedPlayers.includes(player.id)}
                                              onChange={() => handlePlayerSelection(player.id)}
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                          </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                            <input
                                              type="checkbox"
                                              checked={selectedForeignPlayers.includes(player.id)}
                                              onChange={() => handleForeignPlayerSelection(player.id)}
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            {isForeign && (
                                                <span className="ml-1 text-orange-600 text-xs">✓</span>
                                            )}
                                          </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{player.name}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                player.role === 'batsman' ? 'bg-red-100 text-red-800' :
                                                player.role === 'bowler' ? 'bg-green-100 text-green-800' :
                                                player.role === 'all-rounder' ? 'bg-purple-100 text-purple-800' :
                                                player.role === 'wicket-keeper' ? 'bg-yellow-100 text-yellow-800' :
                                                'bg-gray-100 text-gray-800'
                                            }`}>
                                              {player.role}
                                            </span>
                                          </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{player.country || 'N/A'}</td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                              <div className="text-xs space-y-1 text-gray-600">
                                              {player.battingStyle && (
                                                <div>Bat: {player.battingStyle}</div>
                                              )}
                                              {player.bowlingStyle && (
                                                <div>Bowl: {player.bowlingStyle}</div>
                                              )}
                                              {player.battingPosition && (
                                                <div>Position: {player.battingPosition}</div>
                                              )}
                                              {player.bowlingType && (
                                                <div>Type: {player.bowlingType}</div>
                                              )}
                                              {player.keepingStyle && (
                                                <div>Keeping: {player.keepingStyle}</div>
                                              )}
                                            </div>
                                          </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                isVisible ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                              {isVisible ? 'Visible' : 'Hidden'}
                                            </span>
                                          </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                            <button onClick={() => handleDeletePlayer(player.id)} title="Delete Player"><TrashIcon /></button>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        ) : (
                            <div className="text-center py-8 text-gray-500">
                            Select a team to view players
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                      <div className="text-gray-500 mb-4">No players available</div>
                      <p className="text-sm text-gray-400">Use the "Fetch Series Squad" button to load player data.</p>
                    </div>
                  )}
                </div>
              )}
              
              {activeTab === 'raw' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Raw Series Info (JSON)</h2>
                  </div>

                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-800 mb-2">
                      📊 Raw CricAPI Series Data
                    </div>
                    <div className="text-xs text-blue-600">
                      View the complete JSON response from CricAPI for debugging and data verification.
                    </div>
                  </div>

                  <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <pre className="bg-gray-50 text-gray-800 p-6 overflow-x-auto text-sm border border-gray-200">
                    {JSON.stringify(importedData?.raw || {}, null, 2)}
                  </pre>
                  </div>
                </div>
              )}
              
              {activeTab === 'squad' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Raw Series Squad (JSON)</h2>
                  </div>

                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-800 mb-2">
                      👥 Raw CricAPI Squad Data
                    </div>
                    <div className="text-xs text-blue-600">
                      View the complete JSON response from CricAPI squad endpoint for debugging and data verification.
                    </div>
                  </div>

                  {squadData ? (
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                      <pre className="bg-gray-50 text-gray-800 p-6 overflow-x-auto text-sm border border-gray-200">
                      {JSON.stringify(squadData, null, 2)}
                    </pre>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                      <div className="text-gray-500 mb-4">No squad data available</div>
                      <p className="text-sm text-gray-400">Use the "Fetch Series Squad" button above to fetch squad data.</p>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'match_management' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Match Management</h2>
                    <div className="flex flex-wrap items-center gap-3">
                      <button
                        onClick={handleImportMatches}
                        disabled={importingMatches}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          importingMatches
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                      >
                        {importingMatches ? 'Importing...' : 'Import from Schedule'}
                      </button>
                      <button
                        onClick={fetchTournamentMatches}
                        disabled={matchManagementLoading}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          matchManagementLoading
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {matchManagementLoading ? 'Loading...' : 'Refresh Matches'}
                      </button>
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="text-sm font-medium text-amber-900 mb-2">
                      Scrape venues → DB (Wikipedia)
                    </div>
                    <p className="text-xs text-amber-800 mb-3">
                      Paste the Wikipedia article that has the <strong>Fixtures</strong> section (e.g.{' '}
                      <code className="bg-amber-100 px-1 rounded text-[11px]">
                        en.wikipedia.org/wiki/2026_Indian_Premier_League
                      </code>
                      ). We read each match block (teams + ground) and update{' '}
                      <code className="bg-amber-100 px-1 rounded text-[11px]">tournament_match_results.venue</code>{' '}
                      by pairing <strong>Team A vs Team B</strong> with your rows (date used if the same teams play twice).
                      Run <strong>dry run</strong> first, then uncheck to write.
                    </p>
                    <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                      <label className="flex-1 block text-xs text-amber-900">
                        Schedule URL
                        <input
                          type="url"
                          value={venueScrapeUrl}
                          onChange={(e) => setVenueScrapeUrl(e.target.value)}
                          placeholder="https://en.wikipedia.org/wiki/2026_Indian_Premier_League"
                          className="mt-1 w-full px-3 py-2 text-sm border border-amber-300 rounded-lg focus:ring-2 focus:ring-amber-500"
                        />
                      </label>
                      <label className="flex items-center gap-2 text-xs text-amber-900 whitespace-nowrap pb-2">
                        <input
                          type="checkbox"
                          checked={venueScrapeDryRun}
                          onChange={(e) => setVenueScrapeDryRun(e.target.checked)}
                          className="rounded border-amber-400"
                        />
                        Dry run only
                      </label>
                      <button
                        type="button"
                        onClick={handleScrapeScheduleVenues}
                        disabled={venueScrapeLoading}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
                      >
                        {venueScrapeLoading ? 'Working…' : 'Run scrape'}
                      </button>
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-800 mb-2">
                      🎯 Centralized Match Management
                    </div>
                    <div className="text-xs text-blue-600 mb-3">
                      Set match results here to automatically update all Pick'em leagues. Results include winner, total runs, and Man of the Match.
                      <strong>Player of the Match (POM)</strong> is auto-filled when: (1) CricAPI returns it for the match, or (2) you set a <strong>Cricbuzz or ESPN scorecard URL</strong> per match and the cron scrapes it after the match completes. <strong>Cricbuzz is recommended</strong> (e.g. <em>cricbuzz.com/live-cricket-scores/…</em>). You can also set or override POM manually via <strong>Set Result</strong> for any match.
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={async () => {
                          setAutoResultMessage(null);
                          setAutoResultRunning(true);
                          try {
                            const result = await apiService.runAutoResultCheck();
                            const msg = result.message
                              ? `No unprocessed matches found.`
                              : `Checked ${result.checked} match(es), updated ${result.updated}.`;
                            setAutoResultMessage(msg);
                            if (result.detail && result.detail.length > 0) {
                              const details = result.detail.map((d: any) =>
                                d.updated ? `Match ${d.match_id}: updated (winner: ${d.winner})` : `Match ${d.match_id}: ${d.reason || d.error || 'no update'}`
                              ).join(' • ');
                              setAutoResultMessage(prev => (prev ? `${prev} ${details}` : details));
                            }
                            if (result.updated > 0) {
                              await fetchTournamentMatches();
                            }
                          } catch (e: any) {
                            setAutoResultMessage(e?.message || 'Run failed');
                          } finally {
                            setAutoResultRunning(false);
                          }
                        }}
                        disabled={autoResultRunning}
                        className="px-3 py-1.5 text-xs font-medium rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                      >
                        {autoResultRunning ? 'Running…' : 'Run auto-result check now'}
                      </button>
                      {autoResultMessage && (
                        <span className="text-xs text-blue-700">{autoResultMessage}</span>
                      )}
                    </div>
                  </div>

                  {matchManagementFetchError && (
                    <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                      {matchManagementFetchError}
                    </div>
                  )}
                  {matchManagementLoading ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
                      <div className="text-gray-600">Loading matches...</div>
                    </div>
                  ) : tournamentMatches.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                      <div className="text-gray-500 mb-4">No matches in database for this tournament</div>
                      <p className="text-sm text-gray-400 mb-3">
                        Match Management reads <code className="text-xs bg-gray-100 px-1 rounded">tournament_match_results</code> for tournament ID{' '}
                        <strong>{id}</strong>. Opening this tab tries to sync from your saved visible schedule or CricAPI schedule if the table was empty.
                      </p>
                      <p className="text-sm text-gray-400 mb-3">
                        If you use a manual schedule (no CricAPI match list), run your seed SQL against this environment&apos;s database, or use{' '}
                        <strong>Schedule</strong> to show matches to users once (that now syncs IDs into this table correctly).
                      </p>
                      <p className="text-sm text-gray-400">
                        Click <strong>Import from Schedule</strong> to copy matches from saved series info into this table (needed once per environment after deploy). If you use <strong>Add manual schedule</strong>, saving again will also sync.
                      </p>
                    </div>
                  ) : (
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Match</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Venue</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Winner</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Runs</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Man of Match</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-64">Scorecard URL (Cricbuzz recommended)</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                          </tr>
                        </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                          {sortedMatchesForManagement.map((match: any) => (
                              <tr key={match.id} className="hover:bg-gray-50">
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="font-medium text-gray-900">{match.match_name}</div>
                                  <div className="text-xs text-gray-500">ID: {match.match_id}</div>
                              </td>
                                <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-500">
                                {match.match_date ? new Date(match.match_date).toLocaleDateString() : 'TBD'}
                              </td>
                                <td className="px-4 py-2 align-top text-sm text-gray-600 min-w-[180px] max-w-[240px]">
                                  <div className="flex flex-col gap-1">
                                    <input
                                      type="text"
                                      value={
                                        venueDrafts[match.match_id] !== undefined
                                          ? venueDrafts[match.match_id]
                                          : (match.venue || '')
                                      }
                                      onChange={(e) =>
                                        setVenueDrafts((prev) => ({
                                          ...prev,
                                          [match.match_id]: e.target.value
                                        }))
                                      }
                                      placeholder="Stadium, city"
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                                    />
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        const raw =
                                          venueDrafts[match.match_id] !== undefined
                                            ? venueDrafts[match.match_id]
                                            : (match.venue || '');
                                        setVenueSaving(match.match_id);
                                        try {
                                          const result = await apiService.updateTournamentMatchVenue(
                                            Number(id),
                                            match.match_id,
                                            raw
                                          );
                                          setTournamentMatches((prev) =>
                                            prev.map((m) =>
                                              m.match_id === match.match_id
                                                ? { ...m, venue: result.venue ?? null }
                                                : m
                                            )
                                          );
                                          setVenueDrafts((prev) => {
                                            const next = { ...prev };
                                            delete next[match.match_id];
                                            return next;
                                          });
                                        } catch (e: any) {
                                          alert(e?.message || 'Failed to save venue');
                                        } finally {
                                          setVenueSaving(null);
                                        }
                                      }}
                                      disabled={String(venueSaving) === String(match.match_id)}
                                      className="px-2 py-1 text-xs rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 self-start"
                                    >
                                      {String(venueSaving) === String(match.match_id)
                                        ? 'Saving...'
                                        : 'Save venue'}
                                    </button>
                                  </div>
                              </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    match.status === 'completed' ? 'bg-green-100 text-green-800' :
                                    match.status === 'live' ? 'bg-yellow-100 text-yellow-800' :
                                    match.status === 'abandoned' ? 'bg-red-100 text-red-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                  {match.status}
                                </span>
                              </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                {match.winner ? (
                                    <span className="font-medium text-green-600">{match.winner}</span>
                                ) : (
                                    <span className="text-gray-500">Not set</span>
                                )}
                              </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                {match.total_runs ? (
                                    <span className="font-medium text-gray-900">{match.total_runs}</span>
                                ) : (
                                    <span className="text-gray-500">Not set</span>
                                )}
                              </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                {match.man_of_match ? (
                                    <span className="font-medium text-blue-600">{match.man_of_match}</span>
                                ) : (
                                    <span className="text-gray-500">Not set</span>
                                )}
                              </td>
                                <td className="px-4 py-2 align-top">
                                  <div className="flex flex-col gap-1 min-w-[200px]">
                                    <input
                                      type="url"
                                      value={espnUrlDrafts[match.match_id] !== undefined ? espnUrlDrafts[match.match_id] : (match.espn_scorecard_url || '')}
                                      onChange={(e) => setEspnUrlDrafts(prev => ({ ...prev, [match.match_id]: e.target.value }))}
                                      placeholder="https://www.cricbuzz.com/live-cricket-scores/…"
                                      className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500"
                                    />
                                    <button
                                      type="button"
                                      onClick={async () => {
                                        const url = (espnUrlDrafts[match.match_id] !== undefined ? espnUrlDrafts[match.match_id] : (match.espn_scorecard_url || '')).trim() || null;
                                        setEspnUrlSaving(match.match_id);
                                        try {
                                          const result = await apiService.updateMatchEspnUrl(Number(id), match.match_id, url || '');
                                          setTournamentMatches(prev => prev.map(m => m.match_id === match.match_id ? { ...m, espn_scorecard_url: result.espn_scorecard_url ?? null } : m));
                                          setEspnUrlDrafts(prev => { const next = { ...prev }; delete next[match.match_id]; return next; });
                                        } catch (e: any) {
                                          alert(e?.message || 'Failed to save ESPN URL');
                                        } finally {
                                          setEspnUrlSaving(null);
                                        }
                                      }}
                                      disabled={espnUrlSaving === match.match_id}
                                      className="px-2 py-1 text-xs rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 self-start"
                                    >
                                      {espnUrlSaving === match.match_id ? 'Saving...' : 'Save URL'}
                                    </button>
                                  </div>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">
                                  <div className="text-xs text-gray-500">
                                  {match.result_updated_at ? 
                                    new Date(match.result_updated_at).toLocaleString() : 
                                    'Never'
                                  }
                                </div>
                              </td>
                                <td className="px-4 py-2 align-top">
                                <div className="flex flex-col gap-1">
                                  {/* Extension Status: single compact line */}
                                  {matchExtensionStatuses[match.match_id] && (
                                    <div className="text-xs text-gray-600 whitespace-nowrap">
                                      {matchExtensionStatuses[match.match_id].extended_by_admin && (
                                        <span className="text-yellow-600">⏱️ Extended</span>
                                      )}
                                      {matchExtensionStatuses[match.match_id].snapshots_exist && (
                                        <span className="text-red-600 ml-1">🔒 Locked</span>
                                      )}
                                    </div>
                                  )}
                                  {/* Action Buttons: horizontal row */}
                                  <div className="flex flex-wrap items-center gap-1.5">
                                    <button
                                      onClick={() => handleFetchMatchDetails(match.match_id)}
                                      disabled={updatingMatch === match.match_id}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                                        updatingMatch === match.match_id
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                          : 'bg-blue-600 hover:bg-blue-700 text-white'
                                      }`}
                                    >
                                      {updatingMatch === match.match_id ? '...' : 'Fetch Details'}
                                    </button>
                                    <button
                                      onClick={() => handleFetchAndOpenModal(match)}
                                      disabled={updatingMatch === match.match_id}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                                        updatingMatch === match.match_id
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                          : 'bg-green-600 hover:bg-green-700 text-white'
                                      }`}
                                    >
                                      {updatingMatch === match.match_id ? '...' : 'Fetch & Set'}
                                    </button>
                                    <button
                                      onClick={() => openMatchResultModal(match)}
                                      disabled={updatingMatch === match.match_id}
                                      className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                                        updatingMatch === match.match_id
                                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                      }`}
                                    >
                                      {updatingMatch === match.match_id ? '...' : 'Set Result'}
                                    </button>
                                    {matchExtensionStatuses[match.match_id] && (
                                      <>
                                        {!matchExtensionStatuses[match.match_id].snapshots_exist && (
                                          <button
                                            onClick={() => handleExtendMatch(match.match_id)}
                                            disabled={extendingMatch === match.match_id}
                                            className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                                              extendingMatch === match.match_id
                                                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                                  : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                                            }`}
                                          >
                                            {extendingMatch === match.match_id ? '...' : 'Extend'}
                                          </button>
                                        )}
                                        {matchExtensionStatuses[match.match_id].extended_by_admin && (
                                          <button
                                            onClick={() => handleResetMatch(match.match_id)}
                                            disabled={resettingMatch === match.match_id}
                                            className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                                              resettingMatch === match.match_id
                                                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                                  : 'bg-orange-600 hover:bg-orange-700 text-white'
                                            }`}
                                          >
                                            {resettingMatch === match.match_id ? '...' : 'Reset'}
                                          </button>
                                        )}
                                        {matchExtensionStatuses[match.match_id].snapshots_exist && (
                                          <button
                                            onClick={() => handleUnlockAndExtend(match.match_id)}
                                            disabled={unlockingMatch === match.match_id}
                                            className={`px-2 py-1 rounded text-xs font-medium transition-colors whitespace-nowrap ${
                                              unlockingMatch === match.match_id
                                                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                                  : 'bg-red-600 hover:bg-red-700 text-white'
                                            }`}
                                          >
                                            {unlockingMatch === match.match_id ? '...' : '🔓 Unlock'}
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'boosters' && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-4">Tournament Boosters</h2>
                  <p className="text-gray-400 text-sm mb-6">
                    Enable which boosters are available for Budget League in this tournament. Only enabled boosters appear in the user Booster modal. Single use per season = max 1 use per team per tournament (default).
                  </p>
                  {boostersError && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">{boostersError}</div>
                  )}
                  {boostersLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-400"></div>
                      <span className="ml-3 text-gray-400">Loading boosters...</span>
                    </div>
                  ) : (
                    <>
                      <div className="rounded-xl border border-white/10 bg-white/5 overflow-hidden">
                        <table className="min-w-full divide-y divide-white/10">
                          <thead>
                            <tr className="bg-white/5">
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Booster</th>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Description</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase w-32">Enabled</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-400 uppercase w-40">Max uses per league</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-white/10">
                            {availableBoosters.map((b) => {
                              const sid = b.id ?? (b as any).booster_id;
                              const settings = boosterSettings[sid] ?? { is_enabled: false, max_usage_per_league: 1 };
                              return (
                                <tr key={sid} className="hover:bg-white/5">
                                  <td className="px-4 py-3 text-white font-medium">{b.name}</td>
                                  <td className="px-4 py-3 text-gray-400 text-sm max-w-md">{b.description || '—'}</td>
                                  <td className="px-4 py-3 text-center">
                                    <input
                                      type="checkbox"
                                      checked={settings.is_enabled}
                                      onChange={() => setBoosterSettings((prev) => ({
                                        ...prev,
                                        [sid]: { ...prev[sid], is_enabled: !settings.is_enabled, max_usage_per_league: settings.max_usage_per_league }
                                      }))}
                                      className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                                    />
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <input
                                      type="number"
                                      min={1}
                                      value={settings.max_usage_per_league}
                                      onChange={(e) => setBoosterSettings((prev) => ({
                                        ...prev,
                                        [sid]: { ...prev[sid], is_enabled: prev[sid]?.is_enabled ?? false, max_usage_per_league: Math.max(1, parseInt(e.target.value, 10) || 1) }
                                      }))}
                                      className="w-16 px-2 py-1 rounded bg-white/10 border border-white/20 text-white text-center text-sm"
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {availableBoosters.length === 0 && (
                        <p className="text-gray-500 mt-4">No boosters found. Run the seed migration to add the 7 Budget League boosters.</p>
                      )}
                      <div className="mt-6 flex justify-end">
                        <button
                          onClick={async () => {
                            if (!id) return;
                            setBoostersSaving(true);
                            setBoostersError(null);
                            try {
                              const tournamentId = parseInt(id, 10);
                              const payload = availableBoosters.map((b) => {
                                const sid = b.id ?? (b as any).booster_id;
                                const s = boosterSettings[sid] ?? { is_enabled: false, max_usage_per_league: 1 };
                                return { booster_id: sid, is_enabled: s.is_enabled, max_usage_per_league: s.max_usage_per_league };
                              });
                              await apiService.bulkUpdateTournamentBoosters(tournamentId, payload);
                            } catch (err: any) {
                              setBoostersError(err?.message || 'Failed to save');
                            } finally {
                              setBoostersSaving(false);
                            }
                          }}
                          disabled={boostersSaving || availableBoosters.length === 0}
                          className="px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium"
                        >
                          {boostersSaving ? 'Saving...' : 'Save booster settings'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'h2h' && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-4">H2H League Settings</h2>
                  <p className="text-gray-400 text-sm mb-6">
                    Allow users to create Head-to-Head leagues for this tournament. When enabled, league creators can choose single-match or multi-match and (for multi) series length.
                  </p>
                  {h2hError && (
                    <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">{h2hError}</div>
                  )}
                  {h2hLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-400"></div>
                      <span className="ml-3 text-gray-400">Loading H2H settings...</span>
                    </div>
                  ) : (
                    <div className="space-y-6 max-w-xl">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="h2h_enabled"
                          checked={h2hEnabled}
                          onChange={(e) => setH2hEnabled(e.target.checked)}
                          className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500"
                        />
                        <label htmlFor="h2h_enabled" className="text-white font-medium">Enable H2H leagues for this tournament</label>
                      </div>
                      <div>
                        <label className="block text-gray-300 font-medium mb-2">Allowed modes</label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 text-gray-300">
                            <input
                              type="checkbox"
                              checked={h2hAllowedModes.includes('single')}
                              onChange={() => toggleH2hMode('single')}
                              className="rounded border-gray-500 bg-white/10 text-indigo-500"
                            />
                            Single match
                          </label>
                          <label className="flex items-center gap-2 text-gray-300">
                            <input
                              type="checkbox"
                              checked={h2hAllowedModes.includes('multi')}
                              onChange={() => toggleH2hMode('multi')}
                              className="rounded border-gray-500 bg-white/10 text-indigo-500"
                            />
                            Multi match
                          </label>
                        </div>
                      </div>
                      <div>
                        <label className="block text-gray-300 font-medium mb-2">Allowed series lengths (multi only)</label>
                        <div className="flex flex-wrap gap-3">
                          {[3, 5, -1].map((len) => (
                            <label key={len} className="flex items-center gap-2 text-gray-300">
                              <input
                                type="checkbox"
                                checked={h2hAllowedLengths.includes(len)}
                                onChange={() => toggleH2hLength(len)}
                                className="rounded border-gray-500 bg-white/10 text-indigo-500"
                              />
                              {len === -1 ? 'Full tournament' : `${len} matches`}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="pt-4">
                        <button
                          onClick={saveH2hSettings}
                          disabled={h2hSaving}
                          className="px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium"
                        >
                          {h2hSaving ? 'Saving...' : 'Save H2H settings'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'points_table' && id && (
                <div className="mb-8 max-w-4xl">
                  <h2 className="text-2xl font-bold text-white mb-2">Points table (dynamic Pick&apos;em)</h2>
                  <p className="text-gray-400 text-sm mb-4">
                    The scraper tries Cricbuzz, then ESPN, then Wikipedia. Datacenter IPs are often blocked by Cricbuzz
                    or ESPN (403), so <strong className="text-gray-300">Wikipedia is usually the URL that works</strong>
                    — set it for reliable standings. Cricbuzz and ESPN are optional when those pages are reachable or you
                    want an alternate layout. Wikipedia uses full team names — aliases are optional. Example wiki URL:{' '}
                    <a
                      href="https://en.wikipedia.org/wiki/2026_Indian_Premier_League#Points_table"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-300 underline break-all"
                    >
                      IPL 2026 points table
                    </a>
                    . Map schedule names to short labels when needed:{' '}
                    <code className="text-indigo-300">{`{"Chennai Super Kings":["CSK","Chennai"]}`}</code>.
                  </p>
                  {pointsTableMessage && (
                    <div className="mb-4 p-3 bg-amber-500/20 border border-amber-500/40 rounded-lg text-amber-200 text-sm">
                      {pointsTableMessage}
                    </div>
                  )}
                  {pointsTableLoading ? (
                    <div className="text-gray-400 py-8">Loading…</div>
                  ) : (
                    <div className="space-y-6">
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5 space-y-3">
                        <label className="block text-gray-300 text-sm">Wikipedia points table URL (recommended)</label>
                        <input
                          value={ptWikipediaUrl}
                          onChange={(e) => setPtWikipediaUrl(e.target.value)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                          placeholder="https://en.wikipedia.org/wiki/2026_Indian_Premier_League#Points_table"
                        />
                        <label className="block text-gray-300 text-sm">Cricbuzz URL (optional)</label>
                        <input
                          value={ptCricbuzzUrl}
                          onChange={(e) => setPtCricbuzzUrl(e.target.value)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                          placeholder="https://www.cricbuzz.com/cricket-series/.../points-table"
                        />
                        <label className="block text-gray-300 text-sm">ESPN URL (optional)</label>
                        <input
                          value={ptEspnUrl}
                          onChange={(e) => setPtEspnUrl(e.target.value)}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm"
                          placeholder="https://www.espncricinfo.com/series/.../points-table-standings"
                        />
                        <label className="block text-gray-300 text-sm">Team aliases (JSON object)</label>
                        <textarea
                          value={ptAliasesJson}
                          onChange={(e) => setPtAliasesJson(e.target.value)}
                          rows={6}
                          className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm font-mono"
                        />
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            disabled={pointsTableSavingConfig}
                            onClick={async () => {
                              const tournamentId = parseInt(id, 10);
                              setPointsTableSavingConfig(true);
                              setPointsTableMessage(null);
                              try {
                                let aliases: Record<string, string[]> = {};
                                try {
                                  const p = JSON.parse(ptAliasesJson);
                                  if (p && typeof p === 'object' && !Array.isArray(p)) aliases = p;
                                } catch {
                                  throw new Error('Invalid JSON for aliases');
                                }
                                await apiService.updateTournamentPointsTableConfig(tournamentId, {
                                  aliases,
                                  cricbuzzUrl: ptCricbuzzUrl,
                                  espnUrl: ptEspnUrl,
                                  wikipediaUrl: ptWikipediaUrl
                                });
                                setPointsTableMessage('Saved URLs and aliases.');
                              } catch (e: any) {
                                setPointsTableMessage(e?.message || 'Save failed');
                              } finally {
                                setPointsTableSavingConfig(false);
                              }
                            }}
                            className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm disabled:opacity-50"
                          >
                            {pointsTableSavingConfig ? 'Saving…' : 'Save config'}
                          </button>
                          <button
                            type="button"
                            disabled={pointsTableScraping}
                            onClick={async () => {
                              const tournamentId = parseInt(id, 10);
                              setPointsTableScraping(true);
                              setPointsTableMessage(null);
                              try {
                                const res = await apiService.scrapeTournamentPointsTable(tournamentId, {
                                  cricbuzzUrl: ptCricbuzzUrl || undefined,
                                  espnUrl: ptEspnUrl || undefined,
                                  wikipediaUrl: ptWikipediaUrl || undefined
                                });
                                const snap = (res as any)?.snapshot;
                                setPointsTableMessage(
                                  `Snapshot #${snap?.id || '?'} from ${snap?.source || 'unknown'} at ${snap?.captured_at || ''}`
                                );
                                const sn = await apiService.getTournamentPointsSnapshots(tournamentId);
                                setPointsTableSnapshots((sn as any).snapshots || []);
                              } catch (e: any) {
                                setPointsTableMessage(e?.message || 'Scrape failed');
                              } finally {
                                setPointsTableScraping(false);
                              }
                            }}
                            className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm disabled:opacity-50"
                          >
                            {pointsTableScraping ? 'Scraping…' : 'Scrape now'}
                          </button>
                        </div>
                      </div>
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <h3 className="text-white font-semibold mb-3">Recent snapshots</h3>
                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm text-left text-gray-300">
                            <thead>
                              <tr className="border-b border-white/10">
                                <th className="py-2 pr-4">ID</th>
                                <th className="py-2 pr-4">When</th>
                                <th className="py-2 pr-4">Source</th>
                                <th className="py-2 pr-4">Rows</th>
                              </tr>
                            </thead>
                            <tbody>
                              {pointsTableSnapshots.map((s) => (
                                <tr key={s.id} className="border-b border-white/5">
                                  <td className="py-2 pr-4">{s.id}</td>
                                  <td className="py-2 pr-4 whitespace-nowrap">{s.captured_at}</td>
                                  <td className="py-2 pr-4">{s.source}</td>
                                  <td className="py-2 pr-4">
                                    {Array.isArray(s.rows)
                                      ? s.rows.length
                                      : (() => {
                                          try {
                                            const r = typeof s.rows === 'string' ? JSON.parse(s.rows || '[]') : s.rows;
                                            return Array.isArray(r) ? r.length : 0;
                                          } catch {
                                            return 0;
                                          }
                                        })()}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {pointsTableSnapshots.length === 0 && (
                            <p className="text-gray-500 text-sm py-4">No snapshots yet — run Scrape after saving URLs.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'fantasy_points' && (
                <div className="mb-8">
                  <h2 className="text-2xl font-bold text-white mb-2">Fantasy Points Configuration</h2>
                  <p className="text-gray-400 text-sm mb-6">
                    Configure conditional point adjustments applied on top of CricAPI base points for all performance-based leagues (Budget, H2H, Draft) in this tournament.
                  </p>
                  {fpError && <div className="mb-4 p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-300 text-sm">{fpError}</div>}
                  {fpSuccess && <div className="mb-4 p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-300 text-sm">{fpSuccess}</div>}
                  {fpLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-400"></div>
                      <span className="ml-3 text-gray-400">Loading rules...</span>
                    </div>
                  ) : (
                    <div className="space-y-8 max-w-2xl">

                      {/* Playing XI Bonus */}
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <input type="checkbox" id="playing_xi_enabled" checked={fpRules.playing_xi_enabled}
                            onChange={e => updateFpRule('playing_xi_enabled', e.target.checked)}
                            className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500" />
                          <label htmlFor="playing_xi_enabled" className="text-white font-semibold">Playing XI Bonus</label>
                        </div>
                        <p className="text-gray-400 text-xs mb-3">Awarded to every player who appears in the match (batted, bowled, or fielded).</p>
                        <div className="flex items-center gap-3">
                          <label className="text-gray-300 text-sm">Bonus points</label>
                          <input type="number" value={fpRules.playing_xi_bonus_points}
                            onChange={e => updateFpRule('playing_xi_bonus_points', Number(e.target.value))}
                            disabled={!fpRules.playing_xi_enabled}
                            className="w-24 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 disabled:opacity-40" />
                        </div>
                      </div>

                      {/* Winning Team Bonus */}
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <input type="checkbox" id="winning_team_enabled" checked={fpRules.winning_team_enabled}
                            onChange={e => updateFpRule('winning_team_enabled', e.target.checked)}
                            className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500" />
                          <label htmlFor="winning_team_enabled" className="text-white font-semibold">Winning Team Bonus</label>
                        </div>
                        <p className="text-gray-400 text-xs mb-3">Awarded to all players from the winning team.</p>
                        <div className="flex items-center gap-3">
                          <label className="text-gray-300 text-sm">Bonus points</label>
                          <input type="number" value={fpRules.winning_team_bonus_points}
                            onChange={e => updateFpRule('winning_team_bonus_points', Number(e.target.value))}
                            disabled={!fpRules.winning_team_enabled}
                            className="w-24 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 disabled:opacity-40" />
                        </div>
                      </div>

                      {/* Player of the Match */}
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <input type="checkbox" id="pom_enabled" checked={fpRules.pom_enabled}
                            onChange={e => updateFpRule('pom_enabled', e.target.checked)}
                            className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500" />
                          <label htmlFor="pom_enabled" className="text-white font-semibold">Player of the Match Bonus</label>
                        </div>
                        <p className="text-gray-400 text-xs mb-3">Awarded to the Player of the Match once the match result and POM are confirmed.</p>
                        <div className="flex items-center gap-3">
                          <label className="text-gray-300 text-sm">Bonus points</label>
                          <input type="number" value={fpRules.pom_bonus_points}
                            onChange={e => updateFpRule('pom_bonus_points', Number(e.target.value))}
                            disabled={!fpRules.pom_enabled}
                            className="w-24 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 disabled:opacity-40" />
                        </div>
                      </div>

                      {/* Duck Out */}
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <input type="checkbox" id="duck_enabled" checked={fpRules.duck_enabled}
                            onChange={e => updateFpRule('duck_enabled', e.target.checked)}
                            className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500" />
                          <label htmlFor="duck_enabled" className="text-white font-semibold">Duck Out Penalty</label>
                        </div>
                        <p className="text-gray-400 text-xs mb-3">Applied to Batsman, WK, and All-Rounder only (not Bowlers). Any player dismissed for 0 receives this penalty regardless of balls faced.</p>
                        <div className="flex flex-wrap items-center gap-4">
                          <div className="flex items-center gap-2">
                            <label className="text-gray-300 text-sm">Points</label>
                            <input type="number" value={fpRules.duck_points}
                              onChange={e => updateFpRule('duck_points', Number(e.target.value))}
                              disabled={!fpRules.duck_enabled}
                              className="w-24 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 disabled:opacity-40" />
                          </div>
                        </div>
                      </div>

                      {/* Positive SR Bonus */}
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <input type="checkbox" id="sr_bonus_enabled" checked={fpRules.sr_bonus_enabled}
                            onChange={e => updateFpRule('sr_bonus_enabled', e.target.checked)}
                            className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500" />
                          <label htmlFor="sr_bonus_enabled" className="text-white font-semibold">Strike Rate Bonus (all roles)</label>
                        </div>
                        <p className="text-gray-400 text-xs mb-3">Applied to all roles. Requires minimum balls faced. SR = 100 counts as 0 bonus.</p>
                        <div className="mb-3 flex items-center gap-3">
                          <label className="text-gray-300 text-sm">Min balls faced</label>
                          <input type="number" min="0" value={fpRules.sr_bonus_min_balls}
                            onChange={e => updateFpRule('sr_bonus_min_balls', Number(e.target.value))}
                            disabled={!fpRules.sr_bonus_enabled}
                            className="w-24 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 disabled:opacity-40" />
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead><tr className="text-gray-400 border-b border-white/10">
                              <th className="text-left py-1 pr-4">Min SR</th>
                              <th className="text-left py-1 pr-4">Max SR</th>
                              <th className="text-left py-1">Points</th>
                            </tr></thead>
                            <tbody>
                              {(fpRules.sr_bonus_bands || []).map((band: any, i: number) => (
                                <tr key={i} className="border-b border-white/5">
                                  <td className="py-1 pr-4"><input type="number" value={band.min_sr ?? ''} onChange={e => updateBand('sr_bonus_bands', i, 'min_sr', e.target.value)} disabled={!fpRules.sr_bonus_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                  <td className="py-1 pr-4"><input type="number" value={band.max_sr ?? ''} onChange={e => updateBand('sr_bonus_bands', i, 'max_sr', e.target.value)} disabled={!fpRules.sr_bonus_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                  <td className="py-1"><input type="number" value={band.points} onChange={e => updateBand('sr_bonus_bands', i, 'points', e.target.value)} disabled={!fpRules.sr_bonus_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Negative SR Penalty */}
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <input type="checkbox" id="sr_penalty_enabled" checked={fpRules.sr_penalty_enabled}
                            onChange={e => updateFpRule('sr_penalty_enabled', e.target.checked)}
                            className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500" />
                          <label htmlFor="sr_penalty_enabled" className="text-white font-semibold">Strike Rate Penalty (Batsman / WK / AR only)</label>
                        </div>
                        <p className="text-gray-400 text-xs mb-3">Applied only to Batsman, Wicketkeeper, and All-Rounder roles. Requires minimum balls faced.</p>
                        <div className="mb-3 flex items-center gap-3">
                          <label className="text-gray-300 text-sm">Min balls faced</label>
                          <input type="number" min="0" value={fpRules.sr_penalty_min_balls}
                            onChange={e => updateFpRule('sr_penalty_min_balls', Number(e.target.value))}
                            disabled={!fpRules.sr_penalty_enabled}
                            className="w-24 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 disabled:opacity-40" />
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead><tr className="text-gray-400 border-b border-white/10">
                              <th className="text-left py-1 pr-4">Min SR</th>
                              <th className="text-left py-1 pr-4">Max SR</th>
                              <th className="text-left py-1">Points</th>
                            </tr></thead>
                            <tbody>
                              {(fpRules.sr_penalty_bands || []).map((band: any, i: number) => (
                                <tr key={i} className="border-b border-white/5">
                                  <td className="py-1 pr-4"><input type="number" value={band.min_sr ?? ''} onChange={e => updateBand('sr_penalty_bands', i, 'min_sr', e.target.value)} disabled={!fpRules.sr_penalty_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                  <td className="py-1 pr-4"><input type="number" value={band.max_sr ?? ''} onChange={e => updateBand('sr_penalty_bands', i, 'max_sr', e.target.value)} disabled={!fpRules.sr_penalty_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                  <td className="py-1"><input type="number" value={band.points} onChange={e => updateBand('sr_penalty_bands', i, 'points', e.target.value)} disabled={!fpRules.sr_penalty_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Economy Rate */}
                      <div className="bg-white/5 border border-white/10 rounded-xl p-5">
                        <div className="flex items-center gap-3 mb-3">
                          <input type="checkbox" id="economy_enabled" checked={fpRules.economy_enabled}
                            onChange={e => updateFpRule('economy_enabled', e.target.checked)}
                            className="rounded border-gray-500 bg-white/10 text-indigo-500 focus:ring-indigo-500" />
                          <label htmlFor="economy_enabled" className="text-white font-semibold">Economy Rate (bowlers)</label>
                        </div>
                        <p className="text-gray-400 text-xs mb-3">Applies to any player who bowled. Requires minimum overs.</p>
                        <div className="mb-3 flex items-center gap-3">
                          <label className="text-gray-300 text-sm">Min overs</label>
                          <input type="number" min="0" step="0.1" value={fpRules.economy_min_overs}
                            onChange={e => updateFpRule('economy_min_overs', Number(e.target.value))}
                            disabled={!fpRules.economy_enabled}
                            className="w-24 px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:ring-2 focus:ring-indigo-500 disabled:opacity-40" />
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead><tr className="text-gray-400 border-b border-white/10">
                              <th className="text-left py-1 pr-4">Min Economy</th>
                              <th className="text-left py-1 pr-4">Max Economy</th>
                              <th className="text-left py-1">Points</th>
                            </tr></thead>
                            <tbody>
                              {(fpRules.economy_bands || []).map((band: any, i: number) => (
                                <tr key={i} className="border-b border-white/5">
                                  <td className="py-1 pr-4"><input type="number" value={band.min_economy ?? ''} onChange={e => updateBand('economy_bands', i, 'min_economy', e.target.value)} disabled={!fpRules.economy_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                  <td className="py-1 pr-4"><input type="number" value={band.max_economy ?? ''} onChange={e => updateBand('economy_bands', i, 'max_economy', e.target.value)} disabled={!fpRules.economy_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                  <td className="py-1"><input type="number" value={band.points} onChange={e => updateBand('economy_bands', i, 'points', e.target.value)} disabled={!fpRules.economy_enabled} className="w-20 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-xs disabled:opacity-40" /></td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Save */}
                      <div className="pt-2">
                        <button onClick={saveFpRules} disabled={fpSaving}
                          className="px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-medium">
                          {fpSaving ? 'Saving...' : 'Save Fantasy Point Rules'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'pickem_phases' && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Pick&apos;em phases / weeks</h2>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={addPickemPhase}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-indigo-600 hover:bg-indigo-700 text-white"
                      >
                        Add phase
                      </button>
                      <button
                        type="button"
                        onClick={savePickemPhasesConfig}
                        disabled={pickemPhasesSaving}
                        className={`px-4 py-2 rounded-lg text-sm font-medium ${
                          pickemPhasesSaving
                            ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {pickemPhasesSaving ? 'Saving…' : 'Save'}
                      </button>
                      <button
                        type="button"
                        onClick={clearPickemPhasesConfig}
                        disabled={pickemPhasesSaving}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-50"
                      >
                        Clear all
                      </button>
                      <button
                        type="button"
                        onClick={fetchTournamentMatches}
                        disabled={matchManagementLoading}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-white border border-gray-300 text-gray-800 hover:bg-gray-50"
                      >
                        {matchManagementLoading ? 'Loading matches…' : 'Refresh matches'}
                      </button>
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800">
                    <p className="mb-2">
                      Group schedule matches into phases (e.g. weeks). Each match may belong to at most one phase. Unassigned
                      matches still count toward cumulative pick&apos;em standings only.
                    </p>
                    <p className="text-xs text-slate-600">
                      Choose a phase below, then tick matches to assign them. Saving overwrites the tournament config.
                    </p>
                  </div>

                  {pickemPhasesMessage && (
                    <div className="mb-4 text-sm text-slate-700 bg-amber-50 border border-amber-100 px-3 py-2 rounded-lg">
                      {pickemPhasesMessage}
                    </div>
                  )}

                  {matchManagementFetchError && (
                    <div className="mb-4 text-sm text-red-700">{matchManagementFetchError}</div>
                  )}

                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1 space-y-3">
                      <p className="text-sm font-semibold text-gray-700">Phases</p>
                      {pickemPhasesDraft.length === 0 ? (
                        <p className="text-sm text-gray-500">No phases yet. Click &quot;Add phase&quot;.</p>
                      ) : (
                        <ul className="space-y-2">
                          {pickemPhasesDraft.map((ph) => (
                            <li key={ph.id}>
                              <button
                                type="button"
                                onClick={() => setPickemPhaseEditId(ph.id)}
                                className={`w-full text-left px-3 py-2 rounded-lg border text-sm ${
                                  pickemPhaseEditId === ph.id
                                    ? 'border-indigo-500 bg-indigo-50 text-indigo-900'
                                    : 'border-gray-200 bg-white text-gray-800 hover:border-gray-300'
                                }`}
                              >
                                <span className="font-medium">{ph.label}</span>
                                <span className="text-gray-500 ml-2">({ph.match_ids.length} matches)</span>
                              </button>
                              <label className="mt-1 block text-xs text-gray-600">
                                Label
                                <input
                                  type="text"
                                  value={ph.label}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    setPickemPhasesDraft((prev) =>
                                      prev.map((p) => (p.id === ph.id ? { ...p, label: v } : p))
                                    );
                                  }}
                                  className="mt-0.5 w-full px-2 py-1 border border-gray-300 rounded text-sm"
                                />
                              </label>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className="lg:col-span-2">
                      <p className="text-sm font-semibold text-gray-700 mb-2">
                        Matches {pickemPhaseEditId ? `(assign to selected phase)` : '(select a phase)'}
                      </p>
                      {!pickemPhaseEditId ? (
                        <p className="text-sm text-gray-500">Select or add a phase to edit assignments.</p>
                      ) : matchManagementLoading ? (
                        <p className="text-sm text-gray-500">Loading matches…</p>
                      ) : tournamentMatches.length === 0 ? (
                        <p className="text-sm text-gray-500">No matches in DB. Import schedule or add matches first.</p>
                      ) : (
                        <div className="max-h-[480px] overflow-y-auto border border-gray-200 rounded-lg bg-white">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="text-left py-2 px-3 w-10"> </th>
                                <th className="text-left py-2 px-3">Match</th>
                                <th className="text-left py-2 px-3">ID</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tournamentMatches.map((m: any) => {
                                const mid = String(m.match_id ?? m.id ?? '');
                                const inPhase =
                                  pickemPhasesDraft.find((p) => p.id === pickemPhaseEditId)?.match_ids.includes(mid) ??
                                  false;
                                return (
                                  <tr key={mid} className="border-t border-gray-100">
                                    <td className="py-2 px-3">
                                      <input
                                        type="checkbox"
                                        checked={inPhase}
                                        onChange={(e) => togglePickemMatchForPhase(mid, e.target.checked)}
                                        aria-label={`Assign ${m.match_name || mid}`}
                                      />
                                    </td>
                                    <td className="py-2 px-3">
                                      {m.match_name || '—'}
                                      {m.match_date && (
                                        <div className="text-xs text-gray-500">{String(m.match_date).slice(0, 16)}</div>
                                      )}
                                    </td>
                                    <td className="py-2 px-3 font-mono text-xs text-gray-600">{mid}</td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'pickem_activity' && (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Pick&apos;em prediction activity (audit)</h2>
                    <button
                      type="button"
                      onClick={loadPickemPredictionAudit}
                      disabled={pickemAuditEventsLoading}
                      className={`px-4 py-2 rounded-lg text-sm font-medium ${
                        pickemAuditEventsLoading
                          ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                      }`}
                    >
                      {pickemAuditEventsLoading ? 'Loading…' : 'Load activity'}
                    </button>
                  </div>

                  <div className="mb-6 p-4 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-800 space-y-2">
                    <p>
                      Review all saved prediction transactions for a fantasy Pick&apos;em league on this tournament.
                      Filter by league ID (required). Optionally narrow by user or match. Events are recorded when
                      predictions are created, updated, or cleared via the app after the audit table is deployed.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                    <div className="space-y-3 p-4 bg-white border border-gray-200 rounded-lg">
                      <label className="block text-sm font-semibold text-gray-700">
                        Pick&apos;em league
                        <select
                          value={pickemAuditLeagueId}
                          onChange={(e) => setPickemAuditLeagueId(e.target.value)}
                          disabled={pickemAuditLeaguesLoading}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm"
                        >
                          <option value="">{pickemAuditLeaguesLoading ? 'Loading leagues…' : 'Select league…'}</option>
                          {pickemAuditLeagues.map((l) => (
                            <option key={l.id} value={String(l.id)}>
                              #{l.id} — {l.name || 'Unnamed'}
                            </option>
                          ))}
                        </select>
                      </label>
                      {pickemAuditLeaguesError && (
                        <p className="text-sm text-red-600">{pickemAuditLeaguesError}</p>
                      )}
                      {!pickemAuditLeaguesLoading && pickemAuditLeagues.length === 0 && !pickemAuditLeaguesError && (
                        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
                          No Pick&apos;em leagues linked to this tournament were found. Enter a league ID below if the
                          league exists but is not associated with this tournament row.
                        </p>
                      )}
                      <label className="block text-sm font-medium text-gray-700">
                        Override league ID (optional)
                        <input
                          type="text"
                          inputMode="numeric"
                          value={pickemAuditManualLeagueId}
                          onChange={(e) => setPickemAuditManualLeagueId(e.target.value)}
                          placeholder="e.g. 40"
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm font-mono"
                        />
                      </label>
                      <p className="text-xs text-gray-500">
                        If override is filled, it takes precedence over the dropdown.
                      </p>
                    </div>
                    <div className="space-y-3 p-4 bg-white border border-gray-200 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700">
                        Filter by user ID (optional)
                        <input
                          type="text"
                          inputMode="numeric"
                          value={pickemAuditFilterUserId}
                          onChange={(e) => setPickemAuditFilterUserId(e.target.value)}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm font-mono"
                        />
                      </label>
                      <label className="block text-sm font-medium text-gray-700">
                        Filter by match ID (optional)
                        <input
                          type="text"
                          value={pickemAuditFilterMatchId}
                          onChange={(e) => setPickemAuditFilterMatchId(e.target.value)}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm font-mono"
                          placeholder="UUID"
                        />
                      </label>
                      <label className="block text-sm font-medium text-gray-700">
                        Row limit (max 2000)
                        <input
                          type="text"
                          inputMode="numeric"
                          value={pickemAuditLimit}
                          onChange={(e) => setPickemAuditLimit(e.target.value)}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-900 text-sm"
                        />
                      </label>
                    </div>
                  </div>

                  {pickemAuditEventsError && (
                    <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-100 px-3 py-2 rounded-lg">
                      {pickemAuditEventsError}
                    </div>
                  )}

                  <div className="border border-gray-200 rounded-lg bg-white overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-sm text-gray-700">
                      {pickemAuditEventsLoading
                        ? 'Loading…'
                        : `${pickemAuditEvents.length} event${pickemAuditEvents.length === 1 ? '' : 's'} loaded`}
                    </div>
                    {!pickemAuditEventsLoading && pickemAuditEvents.length === 0 && !pickemAuditEventsError && (
                      <p className="p-6 text-sm text-gray-500">Load activity to see results.</p>
                    )}
                    {!pickemAuditEventsLoading && pickemAuditEvents.length > 0 && (
                      <div className="max-h-[560px] overflow-auto">
                        <table className="w-full text-xs sm:text-sm">
                          <thead className="bg-gray-100 sticky top-0">
                            <tr>
                              <th className="text-left py-2 px-2 font-semibold text-gray-700">When (local)</th>
                              <th className="text-left py-2 px-2 font-semibold text-gray-700">User</th>
                              <th className="text-left py-2 px-2 font-semibold text-gray-700">Match</th>
                              <th className="text-left py-2 px-2 font-semibold text-gray-700">Type</th>
                              <th className="text-left py-2 px-2 font-semibold text-gray-700">Pick change</th>
                              <th className="text-left py-2 px-2 font-semibold text-gray-700">Src</th>
                              <th className="text-left py-2 px-2 font-semibold text-gray-700">Txn</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pickemAuditEvents.map((ev: any) => {
                              const when = ev.created_at ? new Date(ev.created_at).toLocaleString() : '—';
                              const userLabel =
                                ev.target_display_name != null && String(ev.target_display_name).trim() !== ''
                                  ? `${ev.target_display_name} (#${ev.target_user_id})`
                                  : `User #${ev.target_user_id}`;
                              const matchLabel = ev.match_name || '—';
                              const matchIdShort = ev.match_id ? String(ev.match_id).slice(0, 8) + '…' : '—';
                              const oldT = ev.old_predicted_winner?.trim() || '—';
                              const newT = ev.new_predicted_winner?.trim() || '—';
                              let change = '';
                              if (ev.event_type === 'created') change = `→ ${newT}`;
                              else if (ev.event_type === 'cleared') change = `${oldT} → (cleared)`;
                              else change = `${oldT} → ${newT}`;
                              const txn = ev.transaction_id
                                ? String(ev.transaction_id).slice(0, 8) + '…'
                                : '—';
                              return (
                                <tr key={ev.id} className="border-t border-gray-100 align-top">
                                  <td className="py-2 px-2 text-gray-800 whitespace-nowrap">{when}</td>
                                  <td className="py-2 px-2 text-gray-800">{userLabel}</td>
                                  <td className="py-2 px-2 text-gray-800">
                                    <div className="max-w-[220px] sm:max-w-xs truncate" title={matchLabel}>
                                      {matchLabel}
                                    </div>
                                    <div className="text-[10px] sm:text-xs text-gray-500 font-mono" title={ev.match_id}>
                                      {matchIdShort}
                                    </div>
                                  </td>
                                  <td className="py-2 px-2 text-gray-800 capitalize">{ev.event_type || '—'}</td>
                                  <td className="py-2 px-2 text-gray-800">{change}</td>
                                  <td className="py-2 px-2 text-gray-600">{ev.source || '—'}</td>
                                  <td className="py-2 px-2 text-gray-600 font-mono" title={ev.transaction_id || ''}>
                                    {txn}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'player_management' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Player Management</h2>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <label className="text-gray-700 text-sm font-medium">Credit Value:</label>
                        <input
                          type="number"
                          value={creditValue}
                          onChange={(e) => setCreditValue(Number(e.target.value))}
                          className="w-20 px-3 py-1 bg-white border border-gray-300 rounded text-gray-900 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                          min="4"
                          max="12"
                          step="0.5"
                        />
                      </div>
                      <button
                        onClick={handleBulkUpdateCredits}
                        disabled={updatingCredits === 'bulk' || selectedPlayers.length === 0}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                          updatingCredits === 'bulk' || selectedPlayers.length === 0
                            ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                            : 'bg-green-600 hover:bg-green-700 text-white'
                        }`}
                      >
                        {updatingCredits === 'bulk' ? 'Updating...' : `Set ${selectedPlayers.length} Players to ${creditValue} Credits`}
                      </button>
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="text-sm text-blue-800 mb-2">
                      💰 Budget League Player Credits
                    </div>
                    <div className="text-xs text-blue-600">
                      Assign credit values to players for budget leagues. Higher credits = higher cost in team building.
                    </div>
                  </div>

                  {selectedPlayers.length > 0 && (
                    <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="text-sm text-yellow-800 mb-2">
                        {selectedPlayers.length} player(s) selected for credit update
                      </div>
                    </div>
                  )}

                                    {/* Filter Controls */}
                  <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="text-sm text-gray-800 mb-3 font-medium">Filter Players</div>
                    <div className="flex flex-wrap gap-4">
                      <div className="flex items-center space-x-2">
                        <label className="text-gray-700 text-sm font-medium">Team:</label>
                        <select
                          value={teamFilter}
                          onChange={(e) => setTeamFilter(e.target.value)}
                          className="px-3 py-1 bg-white border border-gray-300 rounded text-gray-900 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="All">All Teams</option>
                          {importedData?.players && Array.from(new Set(importedData.players.map((player: any) => player.team_name))).sort().map((team: any) => (
                            <option key={team} value={team}>{team}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex items-center space-x-2">
                        <label className="text-gray-700 text-sm font-medium">Role:</label>
                        <select
                          value={roleFilter}
                          onChange={(e) => {
                            console.log('Role filter changed to:', e.target.value);
                            setRoleFilter(e.target.value);
                          }}
                          className="px-3 py-1 bg-white border border-gray-300 rounded text-gray-900 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        >
                          <option value="All">All Roles</option>
                          {importedData?.players && Array.from(new Set(importedData.players.map((player: any) => player.role))).sort().map((role: any) => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </div>
                      <button
                        onClick={() => {
                          setTeamFilter('All');
                          setRoleFilter('All');
                        }}
                        className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm font-medium transition-colors"
                      >
                        Clear Filters
                      </button>
                    </div>
                    {/* Debug Info */}
                    {importedData?.players && (
                      <div className="mt-3 text-xs text-gray-500">
                        Available roles: {Array.from(new Set(importedData.players.map((player: any) => player.role))).sort().join(', ')}
                      </div>
                    )}
                    {/* Filter Summary */}
                    {(() => {
                      const filteredPlayers = importedData?.players?.filter((player: any) => {
                        const teamMatch = teamFilter === 'All' || player.team_name === teamFilter;
                        const roleMatch = roleFilter === 'All' || player.role === roleFilter;
                        return teamMatch && roleMatch;
                      }) || [];
                      const totalPlayers = importedData?.players?.length || 0;
                      const showingPlayers = filteredPlayers.length;
                      
                      if (teamFilter !== 'All' || roleFilter !== 'All') {
                        return (
                          <div className="mt-3 text-xs text-gray-600">
                            Showing {showingPlayers} of {totalPlayers} players
                            {teamFilter !== 'All' && ` (Team: ${teamFilter})`}
                            {roleFilter !== 'All' && ` (Role: ${roleFilter})`}
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>

                  {importedData?.players && importedData.players.length > 0 ? (
                    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-6 py-4 text-left">
                                <input
                                  type="checkbox"
                                  checked={(() => {
                                    const filteredPlayers = importedData.players.filter((player: any) => {
                                      const teamMatch = teamFilter === 'All' || player.team_name === teamFilter;
                                      const roleMatch = roleFilter === 'All' || player.role === roleFilter;
                                      return teamMatch && roleMatch;
                                    });
                                    return selectedPlayers.length === filteredPlayers.length && filteredPlayers.length > 0;
                                  })()}
                                  onChange={(e) => {
                                    const filteredPlayers = importedData.players.filter((player: any) => {
                                      const teamMatch = teamFilter === 'All' || player.team_name === teamFilter;
                                      const roleMatch = roleFilter === 'All' || player.role === roleFilter;
                                      return teamMatch && roleMatch;
                                    });
                                    if (e.target.checked) {
                                      setSelectedPlayers(filteredPlayers.map((player: any) => player.id));
                                    } else {
                                      setSelectedPlayers([]);
                                    }
                                  }}
                                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                />
                              </th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Team</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Credits</th>
                              <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Set Credits</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {importedData.players
                              .filter((player: any) => {
                                const teamMatch = teamFilter === 'All' || player.team_name === teamFilter;
                                const roleMatch = roleFilter === 'All' || player.role === roleFilter;
                                if (roleFilter !== 'All' && !roleMatch) {
                                  console.log('Player filtered out by role:', player.name, 'Role:', player.role, 'Filter:', roleFilter);
                                }
                                return teamMatch && roleMatch;
                              })
                              .map((player: any, idx: number) => (
                              <tr key={player.id || idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <input
                                    type="checkbox"
                                    checked={selectedPlayers.includes(player.id)}
                                    onChange={() => handlePlayerSelection(player.id)}
                                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                  />
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                      <div className="h-10 w-10 rounded-full bg-indigo-500 flex items-center justify-center">
                                        <span className="text-sm font-medium text-white">
                                          {player.name?.charAt(0)?.toUpperCase() || 'P'}
                                        </span>
                                      </div>
                                    </div>
                                    <div className="ml-4">
                                      <div className="text-sm font-medium text-gray-900">{player.name}</div>
                                      <div className="text-sm text-gray-500">{player.country || 'N/A'}</div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                    {player.team_name}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                    player.role === 'batsman' ? 'bg-red-100 text-red-800' :
                                    player.role === 'bowler' ? 'bg-green-100 text-green-800' :
                                    player.role === 'all-rounder' ? 'bg-purple-100 text-purple-800' :
                                    player.role === 'wicket-keeper' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                  }`}>
                                    {player.role}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="text-sm text-gray-700 font-medium">
                                    {playerCredits[player.id] || 9} credits
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center space-x-2">
                                                                      <input
                                    type="number"
                                    value={playerCredits[player.id] || 9}
                                    onChange={(e) => {
                                      const newCredits = Number(e.target.value);
                                      setPlayerCredits(prev => ({
                                        ...prev,
                                        [player.id]: newCredits
                                      }));
                                    }}
                                    className="w-20 px-2 py-1 bg-white border border-gray-300 rounded text-gray-900 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                                    min="4"
                                    max="12"
                                    step="0.5"
                                  />
                                    <button
                                      onClick={() => handleUpdatePlayerCredits(player.id, playerCredits[player.id] || 9)}
                                      disabled={updatingCredits === player.id}
                                      className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                        updatingCredits === player.id
                                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                                          : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                                      }`}
                                    >
                                      {updatingCredits === player.id ? 'Saving...' : 'Save'}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                      <div className="text-gray-500 mb-4">No players available</div>
                      <p className="text-sm text-gray-400">Import player data from the Teams Squad tab first.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>

      {/* Manual Entry Modals */}
      <ManualTeamsModal
        isOpen={showManualTeamsModal}
        onClose={() => setShowManualTeamsModal(false)}
        onAdd={handleAddManualTeams}
      />
      
      <ManualScheduleModal
        isOpen={showManualScheduleModal}
        onClose={() => setShowManualScheduleModal(false)}
        onAdd={handleAddManualSchedule}
        availableTeams={importedData?.teams || []}
      />
      
      <ManualSquadModal
        isOpen={showManualSquadModal}
        onClose={() => setShowManualSquadModal(false)}
        onAdd={handleAddManualSquad}
        availableTeams={importedData?.teams || []}
      />

      <MatchResultModal
        isOpen={showMatchResultModal}
        onClose={() => {
          setShowMatchResultModal(false);
          setSelectedMatch(null);
        }}
        onSave={handleUpdateMatchResult}
        match={selectedMatch}
        updating={updatingMatch !== null}
        tournamentId={id ? Number(id) : undefined}
        onFetchPOM={selectedMatch && id ? async (espnScorecardUrl: string) => {
          const result = await apiService.fetchPOMFromUrl(Number(id), selectedMatch.match_id, espnScorecardUrl);
          if (result?.success && result?.man_of_match) {
            setSelectedMatch((prev: any) => prev ? { ...prev, man_of_match: result.man_of_match } : null);
            await fetchTournamentMatches();
            return { man_of_match: result.man_of_match };
          }
          throw new Error(result?.message || 'Failed to fetch POM');
        } : undefined}
      />
    </div>
  );
};

export default AdminTournamentDetails; 